/* eslint-disable indent */
import React, {
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { FormProvider, useForm, Controller, useFormContext } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { init as initFullStory } from '@fullstory/browser';
import OrderSummary from '../RegularOrder/OrderSummary/OrderSummary';
import PickStoreType from './PickStoreType/PickStoreType';
import StoreCheckoutHeader from './StoreCheckoutHeader/StoreCheckoutHeader';
import StoreDetails from './StoreDetails/StoreDetails';
import StoreReviewOrderStep from './StoreReviewOrderStep/StoreReviewOrderStep';
import StoreConfirmSizes from './StoreConfirmSizes/StoreConfirmSizes';
import type { ISizesFormValues } from './StoreConfirmSizes/helpers/storeConfirmSizes.types';
import type { DesignGroup } from './types/storeReviewOrderStep.types';
import SelectPaymentOption from './SelectPaymentOption/SelectPaymentOption';
import SelectShippingOption from './SelectShippingOption/SelectShippingOption';
import StoreRecentAddress from './StoreRecentAddress/StoreRecentAddress';
import { useStoreRecentAddressSelection } from './helpers/useStoreRecentAddressSelection';
import { useEditModeSafe } from './context/EditModeContext';
import { useStoreCheckoutUiVariationOptional } from './context/StoreCheckoutUiVariationContext';
import {
  STORE_CHECKOUT_FOOTER_SUMMARY_TEXT,
  STORE_CHECKOUT_FOOTER_SUMMARY_TEXT_MOBILE,
  STORE_CHECKOUT_MOBILE_FOOTER_DESCRIPTION,
  STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_MAX_HEIGHT,
  STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_Z_INDEX,
  STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_HEIGHT,
  STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_Z_INDEX,
  STORE_CHECKOUT_STEP_LABELS,
  STORE_ORDER_SUMMARY_TEXT,
  STORE_ORDER_SUMMARY_TEXT_MOBILE,
  getStoreCheckoutUiVariationModifierClass,
  showsStoreCheckoutPaymentOptionStep,
  STORE_CHECKOUT_UI_VARIATION_VALUES,
  STORE_EDIT_SAVE_SUCCESS_MESSAGE,
  STORE_EDIT_STORE_TYPE_LOCKED_BANNER,
  STORE_EDIT_BASELINE_STABILIZATION_MS,
  STORE_EDIT_LEAVE_MODAL_Z_INDEX,
  STORE_EDIT_BANNER_MAIL_ICON_SRC,
  STORE_EDIT_SUPPORT_EMAIL,
  STORE_CHECKOUT_LOADING_TIP,
  STORE_CHECKOUT_LAUNCHING_TIP,
  STORE_CHECKOUT_PLACING_ORDER_TIP,
  STORE_MESSAGES,
  STORE_NEW_SETUP_LEAVE_MODAL_CONTINUE_LABEL,
  STORE_NEW_SETUP_LEAVE_MODAL_DISCARD_LABEL,
  STORE_NEW_SETUP_LEAVE_MODAL_PRIMARY_TEXT,
  STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE,
  STORE_SHIPPING_FORM_LABELS,
  STORE_SHIPPING_TO_LABEL,
  STORE_STATE_SELECT_NOT_FOUND_MESSAGE,
  STORE_STATE_SELECT_SEARCH_PLACEHOLDER,
  STORE_CHECKOUT_DELIVERY_SHIP_TYPE_OPTIONS,
  STORE_SHIPPING_DELIVERY_OPTION_TYPES,
  STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_PREFIX,
  STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_ACTION_LABEL,
  STORE_DEFAULT_SHIPPING_FORM_OPTION,
  STORES_FORM_MIXPANEL_SECTIONS,
} from './constants/storeCheckout.constants';
import {
  getStoreCheckoutLaunchTooltipTitle,
  getStoreLaunchDisabledReason,
  isStoreShippingInfoSectionComplete,
  type IStoreCheckoutShippingDetailsInput,
} from './helpers/storeCheckoutLaunchValidation.utils';
import { buildStoreLaunchPayload } from './helpers/storeCheckoutLaunchPayload.utils';
import {
  openStorePreview,
  resolveStoreCheckoutPreviewStoreId,
} from './helpers/storeCheckoutPreview.utils';
import { useStoreCheckoutShippingTimeline } from './helpers/useStoreCheckoutShippingTimeline';
import {
  buildStoreCheckoutHydrationFromStoreDetails,
  isStoreCheckoutEditPage,
  mergeStoreCheckoutCartItems,
  resolveStoreCheckoutOrderIdFromUrl,
} from './helpers/storeCheckoutStoreDetails.utils';
import { buildStoreReviewDonationAmountsFromCartItems } from './helpers/storeReviewDonationValidation.utils';
import {
  applyApparelCatalogNamesToCartItems,
  needsStoreCheckoutProductTitleEnrichment,
  resolveProductSelectionsStyleCodeCandidates,
} from './helpers/storeCheckoutProductTitleEnrichment.utils';
import {
  buildDesignStoreFormValuesFromStore,
  buildStoreBrandingStateFromStore,
  buildBrandingStateForEditSave,
} from './helpers/storeCheckoutBrandingHydration.utils';
import { resolveStoreShippingOptionFromStore } from './helpers/storeCheckoutShippingTypeUpdate.utils';
import type { StoreShippingFormOption } from './helpers/storeCheckoutShippingTypeUpdate.utils';
import {
  buildStoreCheckoutItemPriceSnapshot,
  haveStoreCartDesignGroupMoqsChanged,
  haveStoreCheckoutItemPricesChanged,
  mapStoreShippingFormOptionToMoqLane,
} from './helpers/storeCheckoutMoqShipping.utils';
import { useStoreShippingAddressPersistence } from './helpers/useStoreShippingAddressPersistence';
import {
  buildStoreCheckoutEditBaseline,
  buildStoreEditSavePayload,
  hasStoreCheckoutEditChanges,
  type IStoreCheckoutEditBaseline,
} from './helpers/storeCheckoutEditChanges.utils';
import { hasNewStoreCheckoutSetupProgress } from './helpers/storeCheckoutNewSetupProgress.utils';
import { resolveStoreCheckoutHasMtoItems } from './helpers/storeCheckoutMto.utils';
import type { IStoreInfoFormShape, IDesignStoreFormShape } from './StoreDetails/storeDetails.types';
import type { IStoreCustomizationBrandingState } from './helpers/storeCheckoutCustomizationPayload.utils';
import { getStoreCartDesignGroupMoqRequestKey } from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import { resolveStoreDetailsFromQueryResult } from '@/utilities/StoreV4ApiUtils';
import ViewportContext from '@/common/context/ViewPort/ViewPortContext';
import { FULLSTORY_ORGID } from '@/constants/Config';
import { ENABLE_TEXT_BLAST_SMS } from '@/constants/feature';
import { getPostStoreLaunchPath } from '@/constants/StoreInvites';
import type {
  IQuantitySizeItem,
  ICustomItem,
  IAddress,
  IReviewOrderItem,
  ICollegiateLicResponse,
  ICheckoutFormData,
  IRegularOrderForm,
  IRegularOrderShippingForm,
} from '@/common/interfaces/order.interface';
import type { ShippingInformation } from '@/common/interfaces/groupOrder.interface';
import useViewport from '@/common/context/ViewPort/useViewPort';
import { useStyleClass } from '@/hooks/useStyleClass';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import Result from '@/components/Result/Result';
import SavePaymentMethodConsent from '@/components/SavePaymentMethodConsent/SavePaymentMethodConsent';
import SavedPaymentMethods from '@/components/SavedPaymentMethods/SavedPaymentMethods';
import StepProgress from '@/components/StepProgress';
import './StoreCheckout.scss';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import Icon from '@/components/Icon/Icon';
import type { StepProgressItem } from '@/components/utils/Interfaces';
import {
  getRegularOrderPayload,
  prepareRegOrderStepsData,
  isCartHasZeroPriceItem,
  getDesignLicensingRecords,
  getWillRequireStatusDesigns,
  getNewTimelineShippingRanges,
  validateShippingZipCode,
} from '@/utilities/CreateOrderUtils';
import {
  MOBILE_SHIPPING_STATE_OVERLAY_STYLE,
  MOBILE_SUMMARY_CLOSE_ICON_STYLE,
  MOBILE_SUMMARY_HALF_OVERLAY_STYLE,
  MOBILE_TAB_DESKTOP_SMALL_VIEWPORT,
  SHIPPING_FIELD_ERROR_MSGS,
  SAVED_PM_RETRY_MSG,
  SAVED_PM_UNAVAILABLE_MSG,
  TABLET_SUMMARY_OVERLAY_STYLE,
} from '@/constants/Order';
import { displayNone, defaultOverlayStyles } from '@/components/utils/DefaultStyles';
import { useGetBraintreeClientTokenQuery } from '@/api/BraintreePaypalQuery';
import {
  useCreateOrderInRedisCacheMutation,
  useCreateRegularOrderMutation,
  useLazyGetCollegiateLicensingInfoQuery,
  useLazyGetCachedOrderQuery,
  useUpdateOrderInRedisCacheMutation,
} from '@/api/OrderQuery';
import { useGetS3PreSignedUrlMutation } from '@/api/S3Query';
import type { IError, ISuccess } from '@/common/interfaces/error.interface';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import Spinner from '@/components/Spinner/Spinner';
import type { RootState } from '@/store';
import Button from '@/components/Button/Button';
import StoreCartPriceChangeWarning from '@/components/StoreCartPriceChangeWarning/StoreCartPriceChangeWarning';
import { useRegularOrderSummary } from '@/hooks/useRegularOrderSummary';
import { Text } from '@/components/Text/Text';
import Overlay from '@/components/Overlay';
import { getLatestRecordBasedOnField, isMobileView } from '@/utilities/CommonUtils.misc';
import {
  buildStateSelectAntdOptions,
  filterStateSelectOption,
  getStateSelectPlaceholder,
} from '@/utilities/StateSelect.utils';
import { setStoreItems, setStoreCartOpened } from '@/store/reducers/storeCart';
import { setCartOpened } from '@/store/reducers/cart';
import { useCartActions } from '@/hooks/useCartActions';
import {
  SavedPaymentMethodUnavailableError,
  useSavedPaymentMethods,
} from '@/hooks/useSavedPaymentMethods';
import {
  captureCheckoutCompleted,
  captureStoreEdited,
  captureStoresFormSectionCompleted,
  captureStoresSetupCompleted,
  resolveCheckoutCompletedPaymentMethod,
} from '@/mixpanel/MixpanelEvents';
import type { ICartForm, ICartItemDetail, ICartResponse } from '@/common/interfaces/cart.interface';
import { updateShipMethodProperties } from '@/store/reducers/shippingMethods';
import { getValidShipMethodsInfo, hasMoreThanThreeColors } from '@/utilities/ShippingUtils';
import { getLatestRestockDate } from '@/utilities/CommonUtils';
import { getCartForm } from '@/utilities/CartUtils';
import useAddressValidation from '@/hooks/useAddressValidation';
import { getUpdatedCartItemsRegOrder, uploadTaxExemptFileToS3 } from '@/utilities/OrderUtils';
import {
  clearPersistedStoreCartDesignGroupMinimums,
  buildStoreCartOpenUrl,
  isStoreCartDrawerViewport,
} from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import { ToolTip } from '@/components/Tooltip/Tooltip';
import { isOrderHasLicensingRejectionItems } from '@/utilities/CreateOrderUtils.misc';
import { withDesignRejectionInfoList } from '@/utilities/LicensingUtils';
import { useGetQuoteMutation } from '@/api/QuoterQuery';
import type { IAdditionalShippingPriceRes } from '@/common/interfaces/quoter.interface';
import { useGetStoreItemsQuery } from '@/api/CartQuery';
import {
  useDeleteStoreMutation,
  useGetStoreDetailsQuery,
  useLaunchStoreMutation,
  useUpdateStoreMutation,
} from '@/api/StoreQuery';
import { useLazyGetDesignItemQuery } from '@/api/DesignQuery';
import { useLazyGetProductSelectionsQuery } from '@/api/ProductInfoQuery';
import type { IDesignItem } from '@/common/interfaces/design.interface';
import { normalizeStoreCartApiResponse } from '@/modules/Cart/StoreCart/helpers/normalizeStoreCartApiResponse';
import { resolveStoreCartApiItemIds } from '@/modules/Cart/StoreCart/helpers/storeCartRemoval.utils';
import { filterCartItemsByStoreOptionSelections } from '@/modules/Cart/StoreCart/helpers/storeCartOptionSelection.utils';
import {
  resetAddressValidationCount,
  setIsSuggestedAddressSelected,
  resetCurrentShippingAddress,
  setIsShippingFormModified,
  setCurrentShippingAddress,
} from '@/store/reducers/order';
import {
  promoCodeUserQuery,
  useGetActiveAppliedPromoCodeUserQuery,
  usePostPromoCodeUserMutation,
  useUpdatePromoCodeUserMutation,
} from '@/api/PromoCodeUserQuery';
import { promoCodeQuery } from '@/api/PromoCodeQuery';
import type { PromoCodeData } from '@/common/interfaces/promoCode.interface';
import type { PromoCodeValidateError } from '@/common/interfaces/promoCodeUser.interface';
import { PROMO_CODE_ERROR_MSG } from '@/constants/Common';
import PromoAppliedOverlay from '@/components/PromoAppliedOverlay/PromoAppliedOverlay';
import { useFeatureFlag } from '@/hooks/useAccount';
import { useBraintree } from '@/components/BraintreeDropIn/BraintreeProvider';
import { UTMContext } from '@/common/context/UTMContext';
import {
  getCheckoutPaymentMethodDetails,
  getSavedPaymentMethodDetails,
  isPaymentMethodPayloadAlreadySaved,
  isSavedPaymentMethodUnavailableError,
} from '@/utilities/PaymentUtils';
import useUpdateCheckoutUrlParams from '@/hooks/useUpdateCheckoutUrlParams';
import { useGetRecentAddressSuggestionsQuery } from '@/api/MostRecentAddressQuery';
import { setConsentGiven } from '@/store/reducers/braintree';
import { useGetTimelineMutation } from '@/api/timelineQuery';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';

initFullStory({ orgId: FULLSTORY_ORGID });
const CustomizeOrderStep = lazy(
  () => import('../RegularOrder/CustomizeOrderStep/CustomizeOrderStep')
);
const UniversityApproval = lazy(() => import('../UniversityApproval/UniversityApproval'));
const PayNow = lazy(() => import('../RegularOrder/PayNow/PayNow'));
const AddressSuggestion = lazy(() => import('@/components/AddressSuggestion/AddressSuggestion'));
const ConfirmationModal = lazy(() => import('@/components/ConfirmationModal/ConfirmationModal'));

interface OrderApiError {
  message: string;
  category: string;
}

const getSavedPaymentMethodAnalyticsName = (
  type: 'credit_card' | 'google_pay' | 'paypal' | 'venmo' | 'unknown'
): string => {
  if (type === 'google_pay') return 'googlePay';
  if (type === 'credit_card') return 'card';
  return type;
};

/** Custom 2-column shipping form layout component */
interface IStoreShippingFormTwoColumnProps {
  onShippingFieldChange?: () => void;
  isReadOnly?: boolean;
}

const StoreShippingFormTwoColumn: React.FC<IStoreShippingFormTwoColumnProps> = ({
  onShippingFieldChange,
  isReadOnly = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const shippingFormStyleClass = useStyleClass('shipping-detail-form');
  const [mobileStateOverlay, setMobileStateOverlay] = useState<boolean>(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState<boolean>(false);
  const isMobile = isMobileView(shippingFormStyleClass);
  const stateSelectOptions = useMemo(() => buildStateSelectAntdOptions(), []);
  const lockedFieldProps = useMemo(
    () =>
      isReadOnly
        ? {
            disabled: true,
          }
        : {},
    [isReadOnly]
  );

  const getFormControlProps = useMemo(
    () => (name: string, label: string, id: string, value: string, icon: string) => ({
      name,
      label,
      id,
      dataAttributes: { id },
      defaultValue: value,
      floatingLabel: true,
      contentBefore: <Icon className={`${icon}-icon`} source={`/src/assets/svg/${icon}.svg`} />,
    }),
    []
  );

  const renderStateField = (isOverlayState = false) => {
    const onDropdownOpen = (isOpen: boolean) => {
      setIsStateDropdownOpen(isOpen);

      if (isOpen && isMobile) {
        setMobileStateOverlay(true);
      }
    };

    const getDropdownStyle = isMobile && !isOverlayState ? displayNone : undefined;

    return (
      <div
        className={`store-shipping-form-two-column__state-field form-field ${
          errors.state?.type === 'required' && 'error'
        }`}
      >
        <Controller
          render={({ field }) => {
            const selectedStateValue =
              typeof field.value === 'object' &&
              field.value !== null &&
              'value' in field.value &&
              field.value.value
                ? field.value.value
                : undefined;
            const stateSelectPlaceholder = getStateSelectPlaceholder(
              selectedStateValue,
              isStateDropdownOpen,
              STORE_SHIPPING_FORM_LABELS.STATE,
              STORE_STATE_SELECT_SEARCH_PLACEHOLDER
            );

            return (
              <Select
                className="state-select-element"
                id="state-select-desktop-tab"
                name={field.name}
                options={stateSelectOptions}
                placeholder={stateSelectPlaceholder}
                value={selectedStateValue}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
                popupClassName="store-state-select-dropdown"
                dropdownAlign={{
                  overflow: {
                    adjustY: false,
                    adjustX: false,
                  },
                }}
                suffixIcon
                showSearch
                optionLabelProp="abbreviation"
                filterOption={filterStateSelectOption}
                notFoundContent={STORE_STATE_SELECT_NOT_FOUND_MESSAGE}
                customSuffixIcon={
                  <Icon
                    className="arrow-down-icon"
                    source="/src/assets/svg/avatar_arrow_down.svg"
                  />
                }
                prefixIcon={<Icon className="map-icon" source="/src/assets/svg/map.svg" />}
                disabled={isReadOnly}
                onChange={(value) => {
                  if (isReadOnly) {
                    return;
                  }

                  field.onChange({
                    value,
                    label: value,
                  });
                  setMobileStateOverlay(false);
                  onShippingFieldChange?.();
                }}
                onBlur={field.onBlur}
                {...(isOverlayState
                  ? {}
                  : {
                      dropdownStyle: getDropdownStyle,
                      onDropdownOpen,
                    })}
                showAction={isMobile ? ['focus'] : ['click']}
                autoFocus={mobileStateOverlay}
              />
            );
          }}
          name="state"
          control={control}
          rules={{
            required: SHIPPING_FIELD_ERROR_MSGS.STATE_REQUIRED,
          }}
        />
        <div
          role="alert"
          className={`form-error ${errors.state?.type === 'required' && 'show-form-error'}`}
        >
          {SHIPPING_FIELD_ERROR_MSGS.STATE_REQUIRED}
        </div>
      </div>
    );
  };

  return (
    <div className="store-shipping-form-two-column">
      <div className="store-shipping-form-two-column__container">
        <div className="store-shipping-form-two-column__field form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'shippingName',
                  STORE_SHIPPING_FORM_LABELS.SHIPPING_NAME,
                  'shipping-name',
                  '',
                  'user'
                )}
                type="text"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="shippingName"
            control={control}
            rules={{
              required: SHIPPING_FIELD_ERROR_MSGS.SHIPPINGNAME_REQUIRED,
              maxLength: {
                value: 35,
                message: SHIPPING_FIELD_ERROR_MSGS.SHIPPINGNAME_MAX,
              },
            }}
          />
        </div>

        <div className="store-shipping-form-two-column__field form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'attentionName',
                  STORE_SHIPPING_FORM_LABELS.ATTENTION_NAME,
                  'attention-name',
                  '',
                  'user'
                )}
                type="text"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="attentionName"
            control={control}
            rules={{
              maxLength: {
                value: 35,
                message: SHIPPING_FIELD_ERROR_MSGS.ATTENTIONNAME_MAX,
              },
            }}
          />
        </div>

        <div className="store-shipping-form-two-column__field store-shipping-form-two-column__field--full-width form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'street2',
                  STORE_SHIPPING_FORM_LABELS.STREET2,
                  'street-2',
                  '',
                  'home'
                )}
                type="text"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="street2"
            control={control}
            rules={{
              maxLength: {
                value: 35,
                message: SHIPPING_FIELD_ERROR_MSGS.STREET2_MAX,
              },
            }}
          />
        </div>

        <div className="store-shipping-form-two-column__field form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'street1',
                  STORE_SHIPPING_FORM_LABELS.STREET1,
                  'street-1',
                  '',
                  'navigation'
                )}
                type="text"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="street1"
            control={control}
            rules={{
              required: SHIPPING_FIELD_ERROR_MSGS.STREET1_REQUIRED,
              maxLength: {
                value: 35,
                message: SHIPPING_FIELD_ERROR_MSGS.STREET1_MAX,
              },
            }}
          />
        </div>

        <div className="store-shipping-form-two-column__field form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'city',
                  STORE_SHIPPING_FORM_LABELS.CITY,
                  'city',
                  '',
                  'tree'
                )}
                type="text"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="city"
            control={control}
            rules={{
              required: SHIPPING_FIELD_ERROR_MSGS.CITY_REQUIRED,
              maxLength: {
                value: 30,
                message: SHIPPING_FIELD_ERROR_MSGS.CITY_MAX,
              },
            }}
          />
        </div>

        {renderStateField()}

        <div className="store-shipping-form-two-column__field form-field">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                {...getFormControlProps(
                  'zip',
                  STORE_SHIPPING_FORM_LABELS.ZIP_CODE,
                  'zip',
                  '',
                  'map_pin'
                )}
                type="number"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onShippingFieldChange?.();
                }}
                onWheel={(e) => {
                  e.preventDefault();
                  e.currentTarget.blur();
                }}
                fieldErrMessg={error?.message ?? ''}
                {...lockedFieldProps}
              />
            )}
            name="shippingZipCode"
            control={control}
            rules={{
              required: SHIPPING_FIELD_ERROR_MSGS.ZIP_REQUIRED,
              validate: (value: string) => validateShippingZipCode(value),
            }}
          />
        </div>
      </div>
      <Overlay
        isOpen={mobileStateOverlay}
        onCloseClick={() => {
          setMobileStateOverlay(false);
        }}
        onBackdropClick={() => {
          setMobileStateOverlay(false);
        }}
        modalClassName={`${shippingFormStyleClass}__state-select-modal`}
        modalStyles={MOBILE_SHIPPING_STATE_OVERLAY_STYLE}
        closeIconPath="/src/assets/svg/close.svg"
        closeIconWrapperStyles={MOBILE_SUMMARY_CLOSE_ICON_STYLE}
      >
        {renderStateField(true)}
      </Overlay>
    </div>
  );
};

const StoreCheckout: React.FC = () => {
  const styleClass = useStyleClass('store-checkout', true);
  const uiVariationCtx = useStoreCheckoutUiVariationOptional();
  const shippingWrapperClass = useStyleClass('store-review-order-step', true);
  const screen = useViewport(true);
  const viewportData = useContext(ViewportContext);
  const viewportWidth = viewportData.width;
  const isMobile = isMobileView(styleClass);
  const { user } = useAppSelector((state: RootState) => state.user);
  const userOrg = user?.accountType !== 'corporate' ? user?.organization ?? '' : '';
  const { storeItems, optionSelectionsByGroupKey } = useAppSelector(
    (state: RootState) => state.storeCart
  );
  const designGroupMoqByKey = useAppSelector(
    (state: RootState) => state.storeCart.designGroupMoqByKey
  );
  const designGroupMoqFetchRequestKey = useAppSelector(
    (state: RootState) => state.storeCart.designGroupMoqFetchRequestKey
  );
  const [reviewItems, setReviewItems] = useState<IReviewOrderItem[]>([]);
  const [quantitySizeItems, setQuantitySizeItems] = useState<IQuantitySizeItem[]>([]);
  const [customItem, setCustomItem] = useState<ICustomItem[]>([]);
  const [stepNumber, setStepNumber] = useState<number>(0);
  const [orderProcessing, setOrderProcessing] = useState<boolean>(false);
  const [canPlaceOrder, setCanPlaceOrder] = useState<boolean>(false);
  const [activeItemRestockDate] = useState<{
    [key: string]: string;
  }>({});
  const [priceZeroCartItem, setPriceZeroCartItem] = useState<boolean>(false);
  const [, setIsOrderHasRejectedItems] = useState<boolean>(false);
  const [isOrderAPIFailed, setIsOrderAPIFailed] = useState<OrderApiError | null>(null);
  const [showMobileSummaryOverlay, setShowMobileSummaryOverlay] = useState<boolean>(false);
  const [showCollegiateInfoForm, setShowCollegiateInfoForm] = useState<boolean>(false);
  const [collegiateLicensingInfo, setCollegiateLicensingInfo] = useState<ICollegiateLicResponse>(
    {} as ICollegiateLicResponse
  );
  const [salesTaxCert, setSalesTaxCert] = useState<File | undefined>();
  const [cartItemsInfo, setCartItemsInfo] = useState<ICartResponse>([]);
  const [isSizesFormValid, setIsSizesFormValid] = useState<boolean>(false);
  const [designGroups, setDesignGroups] = useState<DesignGroup[]>([]);
  const [donationAmounts, setDonationAmounts] = useState<Record<string, string>>({});
  const editPageBaselineRef = useRef<IStoreCheckoutEditBaseline | null>(null);
  const hasCapturedEditPageBaselineRef = useRef(false);
  const hasHydratedStoreDetailsRef = useRef(false);
  const editBrandingStateRef = useRef<IStoreCustomizationBrandingState | null>(null);
  const [isStoreDetailsHydrated, setIsStoreDetailsHydrated] = useState(
    () => !resolveStoreCheckoutOrderIdFromUrl()
  );
  const [isEditPageBaselineReady, setIsEditPageBaselineReady] = useState(false);
  const [isNewStoreLeaveModalOpen, setIsNewStoreLeaveModalOpen] = useState(false);
  const [showShippingMoqReviewWarning, setShowShippingMoqReviewWarning] = useState(false);

  const [createRegularOrder] = useCreateRegularOrderMutation();
  const [createOrderInRedisCache] = useCreateOrderInRedisCacheMutation();
  const [updateOrderInRedisCache] = useUpdateOrderInRedisCacheMutation();
  const [getCachedOrder] = useLazyGetCachedOrderQuery();
  const [deleteStore] = useDeleteStoreMutation();
  const [launchStore] = useLaunchStoreMutation();
  const [updateStore] = useUpdateStoreMutation();
  const [fetchDesignItem] = useLazyGetDesignItemQuery();
  const [fetchProductSelections] = useLazyGetProductSelectionsQuery();
  const apparelCatalogNameByItemIdRef = useRef<Record<number, string>>({});
  const { suggestions, showSuggestions, validateAddressInfo } = useAddressValidation();
  const effectiveStoreId = useMemo(() => resolveStoreCheckoutOrderIdFromUrl(), []);
  const hasStoreIdInUrl = Boolean(effectiveStoreId);

  const {
    data: storeDetailsResponse,
    error: storeDetailsError,
    isSuccess: isStoreDetailsSuccess,
    isError: isStoreDetailsError,
    isLoading: isStoreDetailsLoading,
    isFetching: isStoreDetailsFetching,
  } = useGetStoreDetailsQuery(effectiveStoreId ?? '', {
    skip: !effectiveStoreId,
    refetchOnMountOrArgChange: !isStoreCheckoutEditPage(),
  });

  const resolvedStoreDetails = useMemo(
    () => resolveStoreDetailsFromQueryResult(storeDetailsResponse, storeDetailsError),
    [storeDetailsError, storeDetailsResponse]
  );

  const isStoreDetailsFetchSettled =
    !isStoreDetailsLoading &&
    (isStoreDetailsSuccess || isStoreDetailsError || resolvedStoreDetails != null) &&
    (!isStoreDetailsFetching || resolvedStoreDetails != null);

  const hasStoreDetailOrderItems = (resolvedStoreDetails?.orderItems?.length ?? 0) > 0;

  const {
    data: storeCartApiData,
    isSuccess: isCartFetchedSuccess,
    isError: isCartFetchedError,
    isLoading: isCartInfoLoading,
  } = useGetStoreItemsQuery(
    { isStore: true },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const storeCartApiItems = useMemo(
    () => normalizeStoreCartApiResponse(storeCartApiData),
    [storeCartApiData]
  );

  const selectedStoreCartApiItems = useMemo(
    () => filterCartItemsByStoreOptionSelections(storeCartApiItems, optionSelectionsByGroupKey),
    [storeCartApiItems, optionSelectionsByGroupKey]
  );

  const cartItemsForStoreMerge = useMemo(() => {
    const sourceItems =
      selectedStoreCartApiItems.length > 0 ? selectedStoreCartApiItems : storeItems ?? [];

    return withDesignRejectionInfoList(sourceItems, userOrg);
  }, [selectedStoreCartApiItems, storeItems, userOrg]);

  const storeDetailsCartItems = useMemo(() => {
    if (!resolvedStoreDetails?.orderItems?.length) {
      return [];
    }

    return buildStoreCheckoutHydrationFromStoreDetails(resolvedStoreDetails).cartItems;
  }, [resolvedStoreDetails]);

  const mergedStoreCheckoutItems = useMemo(() => {
    if (!hasStoreIdInUrl || !resolvedStoreDetails) {
      return [];
    }

    return mergeStoreCheckoutCartItems(resolvedStoreDetails, cartItemsForStoreMerge);
  }, [cartItemsForStoreMerge, hasStoreIdInUrl, resolvedStoreDetails]);

  const storeCheckoutActiveCartItems = useMemo(() => {
    if (cartItemsInfo.length > 0) {
      return cartItemsInfo;
    }

    if (mergedStoreCheckoutItems.length > 0) {
      return mergedStoreCheckoutItems;
    }

    if (storeDetailsCartItems.length > 0) {
      return storeDetailsCartItems;
    }

    return selectedStoreCartApiItems;
  }, [cartItemsInfo, mergedStoreCheckoutItems, selectedStoreCartApiItems, storeDetailsCartItems]);

  const hasMtoItems = useMemo(
    () =>
      resolveStoreCheckoutHasMtoItems(
        storeCheckoutActiveCartItems,
        resolvedStoreDetails?.is_made_to_order ?? false
      ),
    [resolvedStoreDetails?.is_made_to_order, storeCheckoutActiveCartItems]
  );

  const savedStoreBranding = useMemo(
    () => (resolvedStoreDetails ? buildStoreBrandingStateFromStore(resolvedStoreDetails) : null),
    [resolvedStoreDetails]
  );

  let checkoutSourceItems = selectedStoreCartApiItems;
  if (hasStoreIdInUrl && resolvedStoreDetails) {
    if (mergedStoreCheckoutItems.length > 0) {
      checkoutSourceItems = mergedStoreCheckoutItems;
    } else if (storeDetailsCartItems.length > 0) {
      checkoutSourceItems = storeDetailsCartItems;
    } else {
      checkoutSourceItems = [];
    }
  }
  const checkoutSourceItemsKey = useMemo(
    () =>
      checkoutSourceItems
        .map((item) => `${item.id}:${item.proofItemId}:${item.designId}`)
        .join('|'),
    [checkoutSourceItems]
  );
  const lastSetupCheckoutSourceItemsKeyRef = useRef<string | null>(null);
  const isCheckoutSourceReady = hasStoreIdInUrl
    ? resolvedStoreDetails != null &&
      (mergedStoreCheckoutItems.length > 0 ||
        storeDetailsCartItems.length > 0 ||
        isCartFetchedSuccess)
    : isCartFetchedSuccess;
  const isCheckoutSourceResolved = hasStoreIdInUrl
    ? isStoreDetailsFetchSettled && (isCheckoutSourceReady || isCartFetchedError)
    : isCartFetchedSuccess || isCartFetchedError;
  /** Fetching the promo code user data */
  const { data: promoCodeUser, refetch: refetchPromoCodeUser } =
    useGetActiveAppliedPromoCodeUserQuery('', {
      refetchOnMountOrArgChange: true,
    });
  const [getCollegiateLicensingInfo] = useLazyGetCollegiateLicensingInfoQuery();
  const [getS3PreSignedUrl] = useGetS3PreSignedUrlMutation();
  const [regOrderNotes, setRegOrderNotes] = useState<{ [key: string]: string }>();
  const [isPayLater] = useState(false);
  const isBraintreeEnabled = useFeatureFlag(user, 'BRAINTREE_PAYMENT_GATEWAY');
  const isVenmoPaymentEnabled = useFeatureFlag(user, 'SHOW_VENMO_PAYMENT_METHOD');
  const isStoreInvitesEnabled = useFeatureFlag(user, ENABLE_TEXT_BLAST_SMS);
  const {
    data: anonymousClientTokenData,
    isLoading: isAnonymousClientTokenLoading,
    isFetching: isAnonymousClientTokenFetching,
    isError: isAnonymousClientTokenError,
  } = useGetBraintreeClientTokenQuery(undefined, {
    skip: !isBraintreeEnabled,
  });
  const checkoutBraintreeClientToken = anonymousClientTokenData?.clientToken ?? '';
  const isCheckoutBraintreeUiLoading =
    isAnonymousClientTokenLoading || isAnonymousClientTokenFetching;
  const utmParams = useContext(UTMContext);
  const [utmState, setUtmState] = useState<Record<string, string>>({});

  const { resetCartItems, resetStoreCartItems } = useCartActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId: editStoreOrderId } = useParams<{ orderId?: string }>();
  const dispatch = useAppDispatch();
  const urlParams = new URLSearchParams(window.location.search);
  const simulateError = urlParams.get('simulateError');
  const showMobileSummary = MOBILE_TAB_DESKTOP_SMALL_VIEWPORT.includes(screen);
  const mobileSummaryOverlayProps = useMemo(
    () => ({
      overlayStyles: {
        bottom: '0',
        top: '0',
        height: STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_HEIGHT,
        pointerEvents: 'auto' as const,
        zIndex: STORE_CHECKOUT_MOBILE_SUMMARY_OVERLAY_Z_INDEX,
      },
      modalStyles: {
        ...MOBILE_SUMMARY_HALF_OVERLAY_STYLE,
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        height: 'auto',
        maxHeight: STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column' as const,
        background: '#FFFDFE',
        borderRadius: '20px 20px 0 0',
        padding: 0,
        overflow: 'hidden',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: STORE_CHECKOUT_MOBILE_SUMMARY_MODAL_Z_INDEX,
      },
    }),
    []
  );
  /** For MOQ logic */
  const [getQuote] = useGetQuoteMutation();
  const [getTimeline] = useGetTimelineMutation();
  const [shipTypeChanged] = useState<string>();
  const [itemShipPriceObj, setItemShipPriceObj] = useState<{
    [key: string]: IAdditionalShippingPriceRes[];
  }>();
  const qtyCompRef = useRef<HTMLDivElement | null>(null);
  const stepsWrapperRef = useRef<HTMLDivElement | null>(null);
  const orderSummaryRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const useAddressValidationState = () => useAppSelector((state: RootState) => state.order);
  const addressValidationState = useAddressValidationState();

  const { updateCheckoutUrlParams } = useUpdateCheckoutUrlParams();

  /** Vars for promo code logic */
  const [postPromoCodeUser] = usePostPromoCodeUserMutation();
  const [updatePromoCodeUser] = useUpdatePromoCodeUserMutation();
  const [promoCodeData, setPromoCodeData] = useState<PromoCodeData>();
  const [promoApplied, setPromoApplied] = useState<boolean>(false);
  const [promoCodeError, setPromoCodeError] = useState<string>('');
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [mobPromoAppliedOverlay, setMobPromoAppliedOverlay] = useState(false);
  const [showBraintreePaymentScreen, setShowBraintreePaymentScreen] = useState(false);
  const [validatingAddress, setValidatingAddress] = useState(false);

  const timelineChangesEnabled = useFeatureFlag(user, 'TIMELINE_GUARANTEE_CHANGES');

  const {
    validateDropIn,
    isDropInInitialized,
    isFormValid,
    updateAmount,
    requestPaymentMethod,
    selectedPaymentMethod,
  } = useBraintree();

  const consentGiven = useAppSelector((state: RootState) => state.braintree.consentGiven);
  const {
    methods: savedMethods,
    selected: selectedSavedMethod,
    isLoading: isSavedMethodsLoading,
    selectSaved: selectSavedMethod,
    useNew: selectNewPaymentMethod,
    getNonceForSelected: getSavedPaymentNonce,
    removeUnavailableMethod: removeUnavailableSavedMethod,
    refetch: refetchSavedMethods,
  } = useSavedPaymentMethods({
    enabled: isBraintreeEnabled,
    userId: user?.id,
  });
  const isUsingSavedPaymentMethod = selectedSavedMethod !== null;
  const isBraintreePaymentReady = isUsingSavedPaymentMethod || (isFormValid && isDropInInitialized);

  const { data: recentAddressSuggestions = [] } = useGetRecentAddressSuggestionsQuery(
    resolvedStoreDetails?.client_id ?? 0,
    {
      skip: !resolvedStoreDetails?.client_id,
      refetchOnMountOrArgChange: true,
    }
  );

  /** Hook to handle the cart items fetched from the API. */
  useEffect(() => {
    if (hasStoreIdInUrl || selectedStoreCartApiItems.length === 0) {
      return;
    }

    const mappedItems = getCartForm(selectedStoreCartApiItems, userOrg);
    setCartItemsInfo(mappedItems);
    dispatch(setStoreItems(mappedItems));
  }, [dispatch, hasStoreIdInUrl, selectedStoreCartApiItems, userOrg]);

  /**
   * This hook helps to check if the user has any designs in the cart that will require approval status.
   */
  useEffect(() => {
    if (cartItemsInfo?.length) {
      const designLicensingRecs = getDesignLicensingRecords(cartItemsInfo);
      const isCartHasWillRequireStatus = getWillRequireStatusDesigns(designLicensingRecs);
      setShowCollegiateInfoForm(user?.accountType === 'collegiate' && isCartHasWillRequireStatus);
    }
  }, [cartItemsInfo, user]);

  /**
   * This hook helps to get the collegiate licensing info from the database.
   */
  useEffect(() => {
    const fetchData = async () => {
      if (showCollegiateInfoForm) {
        const { data: licensingInfoRes } = await getCollegiateLicensingInfo('');
        setCollegiateLicensingInfo(licensingInfoRes || null);
      }
    };

    fetchData();
  }, [getCollegiateLicensingInfo, showCollegiateInfoForm]);

  useEffect(() => {
    if (utmParams) {
      const utmObject: Record<string, string> = {};

      // eslint-disable-next-line react/destructuring-assignment
      utmParams.forEach((value, key) => {
        utmObject[key] = value;
      });

      setUtmState(utmObject);
    }
  }, [utmParams]);

  /** Function to get saved form values
   * @param key - key to get the saved form values from local storage
   * @param defaultReturn - default value to return if no saved form values found
   */
  const getSavedFormValues = useCallback(
    <T extends Record<string, string | number | object | boolean>>(
      key: string,
      defaultReturn: T
    ): T => {
      const savedFormValues = localStorage.getItem(key);

      if (savedFormValues) {
        try {
          const values = JSON.parse(savedFormValues) as Record<string, T>;

          if (user?.id && values[user.id]) {
            return values[user.id];
          }
        } catch {
          return defaultReturn;
        }
      }
      return defaultReturn;
    },
    [user?.id]
  );

  /** Order Name Form */
  // Check if we're on edit page (static data mode)
  const isOnEditPageUrl = window.location.pathname.includes('/store/edit/');

  // Try to get edit mode from context (only available on edit page)
  const editModeCtx = useEditModeSafe();

  // Determine if fields should be disabled:
  // - On create page: never disabled (isOnEditPageUrl = false)
  // - On edit page: store info/branding/sizes stay editable; store type, close date, pricing,
  //   items, payment, and shipping are always locked per BRS/Phase 2 edit flow
  const isEditPage = isOnEditPageUrl;
  const isFieldsDisabled = editModeCtx?.isFieldsDisabled ?? isOnEditPageUrl;
  const isStoreInfoReadOnly = useMemo(() => {
    if (isEditPage) {
      return false;
    }

    return isFieldsDisabled;
  }, [isEditPage, isFieldsDisabled]);

  const isConfirmSizesReadOnly = useMemo(
    () => !isEditPage && isFieldsDisabled,
    [isEditPage, isFieldsDisabled]
  );

  const isReviewOrderStepLocked = useMemo(
    () => isEditPage || isFieldsDisabled,
    [isEditPage, isFieldsDisabled]
  );

  /**
   * The bulk (ship-to-one-place) delivery address stays editable in both the create flow and
   * the live-store edit page — only the shipping method is locked on edit (BR4/TC9 override).
   */
  const isBulkShippingAddressReadOnly = false;

  const storeSizeOrderCounts = useMemo(
    () => resolvedStoreDetails?.sizeOrderCounts ?? {},
    [resolvedStoreDetails?.sizeOrderCounts]
  );

  const defaultStoreTypeValue = useMemo(
    () => ({
      storeType: '' as 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | '',
    }),
    []
  );
  const storeTypeForm = useForm<{
    storeType: 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store' | '';
  }>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultStoreTypeValue,
  });

  const defaultStoreInfoValue = useMemo(
    () => ({
      storeName: '',
      orderUntil: '',
      storeDescription: '',
      displayMadeWith: true,
      charityName: '',
      raiseAmount: '',
    }),
    []
  );
  const storeInfoForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultStoreInfoValue,
  });

  const { watch: watchStoreInfo } = storeInfoForm;
  const storeInfoFormValue = watchStoreInfo();

  const defaultDesignStoreValue = useMemo(
    () => ({
      personalizeToggle: false,
      selectedColor: '#33A687',
      storeLogo: '',
      selectedColors: [] as string[],
      selectedVibe: '',
    }),
    []
  );
  const designStoreForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultDesignStoreValue, // Don't load from cache
  });
  const designStoreFormValue = designStoreForm.watch();

  const defaultOrderNameValue = useMemo(() => ({ orderName: '' }), []);
  const orderNameForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: getSavedFormValues('orderNameForm', defaultOrderNameValue),
  });

  const {
    setValue: setValueOrderName,
    trigger: triggerOrderName,
    watch: watchOrderName,
  } = orderNameForm;
  const orderNameFormValue = watchOrderName();

  /** Review Order Item Form */
  const defaultReviewOrderValue = useMemo(
    () => ({
      confirmOrder: false,
      nonStdPlacementConfirm: false,
      paymentOption: '',
    }),
    []
  );
  const reviewOrderForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultReviewOrderValue, // Don't load from cache
  });

  const { watch: watchReviewOrder } = reviewOrderForm;
  const reviewOrderFormValue = watchReviewOrder();

  /** Confirm Sizes Form */
  const defaultSizesValue = useMemo<ISizesFormValues>(() => ({ sizes: [] }), []);
  const sizesForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues:
      (getSavedFormValues(
        'sizesForm',
        defaultSizesValue as unknown as Record<string, string | number | boolean | object>
      ) as unknown as ISizesFormValues) ?? defaultSizesValue,
  });

  const {
    formState: { isValid: isSizesValid },
    watch: watchSizes,
  } = sizesForm;
  const sizesFormValue = watchSizes();

  /** Shipping Detail Form */
  const defaultShippingDetailsValue = useMemo(
    () => ({
      shippingName: '',
      attentionName: '',
      street1: '',
      street2: '',
      city: '',
      state: { label: '', value: '' },
      shippingZipCode: '',
      suggestions: [] as IAddress[],
      showSuggestions: false,
      shipType: '',
      shipTypeKey: STORE_SHIPPING_DELIVERY_OPTION_TYPES.STANDARD as string,
      shipTypeTitle: '',
      shippingCost: 0,
      shippingOption: resolvedStoreDetails
        ? resolveStoreShippingOptionFromStore(resolvedStoreDetails)
        : STORE_DEFAULT_SHIPPING_FORM_OPTION,
    }),
    [resolvedStoreDetails]
  );

  const shippingDetailsForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultShippingDetailsValue, // Don't load from cache
  });

  // Reset form on API callback - but not on edit page to preserve dirty tracking
  useEffect(() => {
    if (!isOnEditPageUrl && !hasStoreIdInUrl) {
      shippingDetailsForm.reset(defaultShippingDetailsValue);
    }
  }, [defaultShippingDetailsValue, hasStoreIdInUrl, shippingDetailsForm, isOnEditPageUrl]);

  const {
    formState: { isValid: isShippingDetailsValid },
    watch: watchShippingDetails,
    setValue: setShippingDetails,
  } = shippingDetailsForm;
  const shippingDetailsFormValue = watchShippingDetails();

  const moqShippingLane = useMemo(
    () => mapStoreShippingFormOptionToMoqLane(shippingDetailsFormValue.shippingOption),
    [shippingDetailsFormValue.shippingOption]
  );

  const pendingShippingMoqReviewRef = useRef<{
    moqSnapshot: Record<string, number>;
    priceSnapshot: Record<number, number>;
    expectedMoqRequestKey: string;
  } | null>(null);

  const scrollToFirstReviewDesignGroup = useCallback(() => {
    window.requestAnimationFrame(() => {
      document.getElementById('store-review-first-design-group')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const handleShippingMoqReviewNowClick = useCallback(() => {
    setShowShippingMoqReviewWarning(false);
    scrollToFirstReviewDesignGroup();
  }, [scrollToFirstReviewDesignGroup]);

  const handleShippingLaneRequoteComplete = useCallback(
    (quotedItems: ICartItemDetail[]) => {
      const pending = pendingShippingMoqReviewRef.current;

      if (!pending || isFieldsDisabled) {
        return;
      }

      if (designGroupMoqFetchRequestKey !== pending.expectedMoqRequestKey) {
        return;
      }

      pendingShippingMoqReviewRef.current = null;

      const moqChanged = haveStoreCartDesignGroupMoqsChanged(
        pending.moqSnapshot,
        designGroupMoqByKey
      );
      const pricesChanged = haveStoreCheckoutItemPricesChanged(pending.priceSnapshot, quotedItems);

      if (moqChanged || pricesChanged) {
        setShowShippingMoqReviewWarning(true);
      }
    },
    [designGroupMoqByKey, designGroupMoqFetchRequestKey, isFieldsDisabled]
  );

  const handleUserShippingOptionChange = useCallback(
    (
      nextShippingOption: StoreShippingFormOption,
      previousShippingOption?: StoreShippingFormOption
    ) => {
      if (
        isFieldsDisabled ||
        !previousShippingOption ||
        previousShippingOption === nextShippingOption
      ) {
        return;
      }

      const nextLane = mapStoreShippingFormOptionToMoqLane(nextShippingOption);
      const expectedMoqRequestKey = getStoreCartDesignGroupMoqRequestKey(
        storeCheckoutActiveCartItems,
        nextLane
      );

      if (!expectedMoqRequestKey) {
        return;
      }

      pendingShippingMoqReviewRef.current = {
        moqSnapshot: { ...designGroupMoqByKey },
        priceSnapshot: buildStoreCheckoutItemPriceSnapshot(storeCheckoutActiveCartItems),
        expectedMoqRequestKey,
      };
    },
    [designGroupMoqByKey, isFieldsDisabled, storeCheckoutActiveCartItems]
  );

  const getShippingFormValues = useCallback(
    () => shippingDetailsForm.getValues(),
    [shippingDetailsForm]
  );

  const hasTrackedShippingInfoSectionRef = useRef(false);

  const buildShippingDetailsInputFromForm = useCallback((): IStoreCheckoutShippingDetailsInput => {
    const values = getShippingFormValues();

    return {
      shippingOption: values.shippingOption ?? '',
      shippingName: values.shippingName ?? '',
      street1: values.street1 ?? '',
      city: values.city ?? '',
      state: values.state,
      shippingZipCode: values.shippingZipCode ?? '',
    };
  }, [getShippingFormValues]);

  const maybeTrackShippingInfoSectionCompleted = useCallback(() => {
    if (hasTrackedShippingInfoSectionRef.current || isFieldsDisabled || isEditPage) {
      return;
    }

    if (!isStoreShippingInfoSectionComplete(buildShippingDetailsInputFromForm())) {
      return;
    }

    hasTrackedShippingInfoSectionRef.current = true;
    captureStoresFormSectionCompleted(
      user?.email ?? '',
      STORES_FORM_MIXPANEL_SECTIONS.SHIPPING_INFO
    );
  }, [buildShippingDetailsInputFromForm, isEditPage, isFieldsDisabled, user?.email]);

  const { persistShippingAddress } = useStoreShippingAddressPersistence({
    storeId: effectiveStoreId,
    isReadOnly: isFieldsDisabled,
    getShippingFormValues,
  });

  const handleShippingAddressFieldChange = useCallback(() => {
    persistShippingAddress();
    maybeTrackShippingInfoSectionCompleted();
  }, [maybeTrackShippingInfoSectionCompleted, persistShippingAddress]);

  const handleUserShippingOptionChangeWithTracking = useCallback(
    (
      nextShippingOption: StoreShippingFormOption,
      previousShippingOption?: StoreShippingFormOption
    ) => {
      handleUserShippingOptionChange(nextShippingOption, previousShippingOption);
      maybeTrackShippingInfoSectionCompleted();
    },
    [handleUserShippingOptionChange, maybeTrackShippingInfoSectionCompleted]
  );

  const { selectedAddressId, handleRecentAddressSelect } = useStoreRecentAddressSelection({
    recentAddresses: recentAddressSuggestions,
    shippingForm: shippingDetailsForm,
    isReadOnly: isFieldsDisabled,
    onAddressApplied: handleShippingAddressFieldChange,
  });

  /** Hydrate checkout forms from store details when arriving from create-store or edit flow. */
  useEffect(() => {
    if (!hasStoreIdInUrl || !resolvedStoreDetails || hasHydratedStoreDetailsRef.current) {
      return;
    }

    const hydration = buildStoreCheckoutHydrationFromStoreDetails(resolvedStoreDetails);

    hasHydratedStoreDetailsRef.current = true;

    storeTypeForm.reset({ storeType: hydration.storeType });
    storeInfoForm.reset({
      ...storeInfoForm.getValues(),
      ...hydration.storeInfo,
    });

    if (hydration.storeInfo.storeName) {
      setValueOrderName('orderName', hydration.storeInfo.storeName);
    }

    shippingDetailsForm.reset({
      ...shippingDetailsForm.getValues(),
      shippingOption: hydration.shippingOption,
      shippingName: resolvedStoreDetails.shipping_name ?? '',
      attentionName: resolvedStoreDetails.attention_name ?? '',
      street1: resolvedStoreDetails.street_1 ?? '',
      street2: resolvedStoreDetails.street_2 ?? '',
      city: resolvedStoreDetails.city ?? '',
      state: resolvedStoreDetails.state
        ? { label: resolvedStoreDetails.state, value: resolvedStoreDetails.state }
        : shippingDetailsForm.getValues('state'),
      shippingZipCode: resolvedStoreDetails.zip_code ?? '',
    });

    const designStoreValues = buildDesignStoreFormValuesFromStore(resolvedStoreDetails);

    if (Object.keys(designStoreValues).length > 0) {
      designStoreForm.reset({
        ...designStoreForm.getValues(),
        ...designStoreValues,
      });
    }

    editBrandingStateRef.current = buildStoreBrandingStateFromStore(resolvedStoreDetails);

    if (isStoreCheckoutEditPage()) {
      setCartItemsInfo(hydration.cartItems);
      dispatch(setStoreItems(hydration.cartItems));
    }

    if (Object.keys(hydration.donationAmounts).length > 0) {
      setDonationAmounts(hydration.donationAmounts);
    }

    setIsStoreDetailsHydrated(true);
  }, [
    dispatch,
    hasStoreIdInUrl,
    resolvedStoreDetails,
    setValueOrderName,
    designStoreForm,
    shippingDetailsForm,
    storeInfoForm,
    storeTypeForm,
  ]);

  /** Backfill donation/markup inputs from cart lines if hydration raced ahead of merge. */
  useEffect(() => {
    if (!hasStoreIdInUrl || Object.keys(donationAmounts).length > 0 || cartItemsInfo.length === 0) {
      return;
    }

    const nextDonationAmounts = buildStoreReviewDonationAmountsFromCartItems(cartItemsInfo);

    if (Object.keys(nextDonationAmounts).length > 0) {
      setDonationAmounts(nextDonationAmounts);
    }
  }, [cartItemsInfo, donationAmounts, hasStoreIdInUrl]);

  /**
   * Edit-store order items often arrive with empty product_title and design.apparel
   * that only has color/style — fill Product Title from apparel.name via proof-item
   * details, then product selections by style code.
   */
  useEffect(() => {
    if (!isEditPage || cartItemsInfo.length === 0) {
      return undefined;
    }

    const cachedNames = apparelCatalogNameByItemIdRef.current;
    const itemsWithCachedNames = applyApparelCatalogNamesToCartItems(cartItemsInfo, cachedNames);

    if (itemsWithCachedNames !== cartItemsInfo) {
      setCartItemsInfo(itemsWithCachedNames);
      dispatch(setStoreItems(itemsWithCachedNames));
      return undefined;
    }

    const itemsNeedingEnrichment = cartItemsInfo.filter(
      (item) => needsStoreCheckoutProductTitleEnrichment(item) && cachedNames[item.id] == null
    );

    if (itemsNeedingEnrichment.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const resolveApparelNameForItem = async (item: ICartItemDetail): Promise<string> => {
      if (item.proofId > 0 && item.proofItemId > 0) {
        try {
          const response = await fetchDesignItem({
            proofId: item.proofId,
            proofItemId: item.proofItemId,
          }).unwrap();
          const designItem = response?.proofItem?.[0] as IDesignItem | undefined;
          const apparelName = designItem?.apparel?.name?.trim() ?? '';

          if (apparelName) {
            return apparelName;
          }
        } catch {
          /* fall through to product selections */
        }
      }

      const styleCandidates = resolveProductSelectionsStyleCodeCandidates(
        item.apparelStyleCode ?? ''
      );

      const styleLookupResults = await Promise.all(
        styleCandidates.map(async (styleCode) => {
          try {
            const selections = await fetchProductSelections({ styleCode }).unwrap();

            return selections?.name?.trim() ?? '';
          } catch {
            return '';
          }
        })
      );

      return styleLookupResults.find((catalogName) => Boolean(catalogName)) ?? '';
    };

    const enrichMissingProductTitles = async () => {
      const resolvedNames = await Promise.all(
        itemsNeedingEnrichment.map(async (item) => {
          const catalogName = await resolveApparelNameForItem(item);

          return { itemId: item.id, catalogName };
        })
      );

      if (cancelled) {
        return;
      }

      const nextCachedNames = { ...apparelCatalogNameByItemIdRef.current };

      resolvedNames.forEach(({ itemId, catalogName }) => {
        // Cache successes and empty failures so we do not refetch forever.
        nextCachedNames[itemId] = catalogName;
      });

      apparelCatalogNameByItemIdRef.current = nextCachedNames;

      setCartItemsInfo((previousItems) => {
        const nextItems = applyApparelCatalogNamesToCartItems(previousItems, nextCachedNames);

        if (nextItems !== previousItems) {
          dispatch(setStoreItems(nextItems));
        }

        return nextItems;
      });
    };

    enrichMissingProductTitles().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [cartItemsInfo, dispatch, fetchDesignItem, fetchProductSelections, isEditPage]);

  /** Credit Card Form */
  const defaultCreditCardValue = useMemo(
    () => ({
      amount: '',
      name: '',
      creditCard: '',
      expiryDate: '',
      cvv: '',
      zipCode: '',
    }),
    []
  );
  const creditCardForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultCreditCardValue,
  });

  const { watch: watchCreditCard, setValue: setValueCreditCard } = creditCardForm;
  const creditCardFormValue = watchCreditCard();

  /** Pay Later Form */
  const defaultPayLaterValue = useMemo(() => ({ canChargeAccount: false, ccEmail: '' }), []);
  const payLaterForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultPayLaterValue,
  });

  const { watch: watchPayLater } = payLaterForm;
  const payLaterFormValue = watchPayLater();

  /** Order Summary Form */
  const defaultSummaryValue = useMemo(
    () => ({
      subTotal: 0,
      shipping: 0,
      salesTax: 0,
      total: 0,
      isSalesTaxSuccess: false,
      isSalesTaxError: false,
      isSalesTaxLoading: false,
      shipTypeKey: 'standard',
      discountedTotal: 0,
      guarantee: '0',
      promoCode: '',
    }),
    []
  );
  const summaryForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultSummaryValue,
  });

  const { watch: watchSummary, setValue: setOrderSummary } = summaryForm;
  const summaryFormValue = watchSummary();

  /** Custom Items Form */
  const defaultCSVFileValue = useMemo(() => ({ customItem: {} }), []);
  const csvFileForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: getSavedFormValues('csvFileForm', defaultCSVFileValue),
  });

  const { watch: watchCsvForm } = csvFileForm;
  const csvFileFormValue = watchCsvForm();

  const {
    subTotal,
    shipping,
    salesTax,
    discountSalesTax,
    total,
    isSalesTaxSuccess,
    isSalesTaxError,
    isSalesTaxLoading,
    shippingType,
    discountedTotal,
    totalFlashShipPrice,
    totalExpeditedShipPrice,
  } = useRegularOrderSummary({
    items: reviewItems,
    sizeFormValues: { sizes: {} },
    shippingFormValues: shippingDetailsFormValue as IRegularOrderShippingForm,
    fetchSalesTax: isShippingDetailsValid && (isSizesValid || isSizesFormValid),
    promoCodeData:
      promoCodeUser?.promoCode && !(promoCodeUser.promoCode as PromoCodeValidateError).message
        ? (promoCodeUser.promoCode as PromoCodeData)
        : undefined,
    guarantee: summaryFormValue.guarantee,
  });

  /** Set order summary form values from useRegularOrderSummary hook */
  useEffect(() => {
    setOrderSummary('subTotal', subTotal);
    setOrderSummary('shipping', shipping);
    if (promoCodeData && promoCodeData?.value > 0) {
      setOrderSummary('salesTax', discountSalesTax);
    } else {
      setOrderSummary('salesTax', salesTax);
    }
    setOrderSummary('total', total);
    setOrderSummary('isSalesTaxSuccess', isSalesTaxSuccess);
    setOrderSummary('isSalesTaxLoading', isSalesTaxLoading);
    setOrderSummary('isSalesTaxError', isSalesTaxError);
    setOrderSummary('shipTypeKey', shippingType);
    setOrderSummary('discountedTotal', discountedTotal);
  }, [
    subTotal,
    shipping,
    dispatch,
    salesTax,
    discountSalesTax,
    total,
    isSalesTaxError,
    isSalesTaxSuccess,
    isSalesTaxLoading,
    setOrderSummary,
    shippingType,
    discountedTotal,
    promoCodeData,
  ]);

  useEffect(() => {
    /** Validate form on load and when Drop-in initializes */
    validateDropIn();
  }, [validateDropIn]);

  useEffect(() => {
    updateAmount(String(total));
  }, [total, updateAmount]);

  /** Cart Details Form */
  const cartDetailsForm = useForm<ICartForm>({
    mode: 'all',
  });

  const { setValue: setCartDetails, getValues: getCartDetailsValue } = cartDetailsForm;

  useEffect(() => {
    let isMoreThan3Colors = false;
    if (cartItemsInfo?.length) {
      isMoreThan3Colors = hasMoreThanThreeColors(cartItemsInfo);

      if (isMoreThan3Colors) {
        cartItemsInfo.forEach((_item, i) => {
          setCartDetails(
            `cartDetails.${i}.shipType`,
            getCartDetailsValue('shipTypeKey') === 'flash'
              ? 'standard'
              : getCartDetailsValue('shipTypeKey')
          );
        });
      }
    }

    dispatch(
      updateShipMethodProperties({
        shipTypeKey: 'flash',
        properties: {
          price: Number(totalFlashShipPrice.toFixed(2)),
        },
      })
    );

    if (shippingDetailsFormValue.shipTypeKey === 'flash') {
      setShippingDetails('shippingCost', totalFlashShipPrice);
    }
  }, [
    totalFlashShipPrice,
    dispatch,
    setShippingDetails,
    shippingDetailsFormValue.shipTypeKey,
    cartItemsInfo,
    setCartDetails,
    getCartDetailsValue,
  ]);

  useEffect(() => {
    dispatch(
      updateShipMethodProperties({
        shipTypeKey: 'expedited',
        properties: { price: Number(totalExpeditedShipPrice.toFixed(2)) },
      })
    );

    if (shippingDetailsFormValue.shipTypeKey === 'expedited') {
      setShippingDetails('shippingCost', totalExpeditedShipPrice);
    }
  }, [totalExpeditedShipPrice, dispatch, setShippingDetails, shippingDetailsFormValue.shipTypeKey]);

  /**
   * This sets the ship type properties based on the cart items
   */
  const setShipTypes = useCallback(
    (cartItemsRecords: ICartResponse) => {
      const { shipTypeProperties } = getValidShipMethodsInfo(cartItemsRecords);

      shipTypeProperties?.forEach((type) => type && dispatch(updateShipMethodProperties(type)));

      const currentShipType = shippingDetailsForm.getValues('shipTypeKey');

      /** Only update the shipTypeKey if it's not already set or different */
      if (
        cartItemsRecords.length > 0 &&
        cartItemsRecords[0]?.shipType &&
        (!currentShipType || currentShipType !== cartItemsRecords[0].shipType)
      ) {
        setShippingDetails('shipTypeKey', cartItemsRecords[0].shipType);
      }
    },
    [dispatch, setShippingDetails, shippingDetailsForm]
  );

  /** University approval Form Initialization starts. */
  const defaultUniversityApprovalFormValue = useMemo(
    () => ({
      fullName: '',
      email: '',
      phone: '',
      schoolInfo: '',
      advisorName: '',
      advisorPhone: '',
      quantity: '',
      whoPaying: '',
      generalPurpose: '',
    }),
    []
  );

  const universityApprovalForm = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: defaultUniversityApprovalFormValue,
  });

  const { watch: watchUniversityApproval, setValue: setUniversityApproval } =
    universityApprovalForm;

  const universityApprovalFormValue = watchUniversityApproval();
  /** University approval Form Initialization ends. */

  /**
   * Hook to set the state of the collegiate Approval form.
   */
  useEffect(() => {
    if (user) {
      const collegiateInfo = {
        fullName: user.full_name,
        email: user.email,
        phone: user.phone,
        schoolInfo: user.school,
        advisorName: '',
        advisorPhone: '',
        quantity: '',
        whoPaying: '',
        generalPurpose: '',
        orgInfo: user.organization,
        schoolId: user.school_id,
      };

      const latestProofItem = getLatestRecordBasedOnField(cartItemsInfo, 'createdAt');

      if (latestProofItem) {
        if (latestProofItem.school?.name) {
          collegiateInfo.schoolInfo = latestProofItem.school.name;
          collegiateInfo.schoolId = latestProofItem.school.id;
        }

        if (latestProofItem.organization?.name) {
          collegiateInfo.orgInfo = latestProofItem.organization.name;
        }
      }

      /**
       * The following info are pre-filled from the info in the database of the most recent order with collegiate licensing by the Account or Individual user.
       */
      if (collegiateLicensingInfo) {
        collegiateInfo.advisorName = collegiateLicensingInfo.advisor_name;
        collegiateInfo.advisorPhone = collegiateLicensingInfo.advisor_phone;
        collegiateInfo.quantity = collegiateLicensingInfo.quantity;
        collegiateInfo.whoPaying = collegiateLicensingInfo.payer;
        collegiateInfo.generalPurpose = collegiateLicensingInfo.purpose;
      }

      universityApprovalForm.reset(collegiateInfo);
    }
  }, [user, setUniversityApproval, universityApprovalForm, collegiateLicensingInfo, cartItemsInfo]);

  const currentFormValues = useMemo(
    () => ({
      ...summaryFormValue,
      ...sizesFormValue,
      ...orderNameFormValue,
      ...creditCardFormValue,
      ...reviewOrderFormValue,
      ...shippingDetailsFormValue,
      ...csvFileFormValue,
      ...universityApprovalFormValue,
      ...payLaterFormValue,
    }),
    [
      summaryFormValue,
      sizesFormValue,
      orderNameFormValue,
      creditCardFormValue,
      reviewOrderFormValue,
      shippingDetailsFormValue,
      csvFileFormValue,
      universityApprovalFormValue,
      payLaterFormValue,
    ]
  );

  const editPageCartItems = useMemo(
    () => (cartItemsInfo.length > 0 ? cartItemsInfo : storeItems),
    [cartItemsInfo, storeItems]
  );

  useEffect(() => {
    if (!isEditPage || hasCapturedEditPageBaselineRef.current) {
      return undefined;
    }

    if (editPageCartItems.length === 0 || designGroups.length === 0) {
      return undefined;
    }

    const syncedSizes = sizesFormValue.sizes ?? [];
    if (syncedSizes.length === 0) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      if (hasCapturedEditPageBaselineRef.current) {
        return;
      }

      editPageBaselineRef.current = buildStoreCheckoutEditBaseline({
        storeInfo: storeInfoFormValue as IStoreInfoFormShape,
        designStore: designStoreFormValue,
        sizes: sizesFormValue as ISizesFormValues,
        cartItems: editPageCartItems,
        shipping: {
          option: shippingDetailsFormValue.shippingOption ?? '',
          address: shippingDetailsFormValue,
        },
      });
      hasCapturedEditPageBaselineRef.current = true;
      setIsEditPageBaselineReady(true);
    }, STORE_EDIT_BASELINE_STABILIZATION_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    designGroups.length,
    designStoreFormValue,
    editPageCartItems,
    isEditPage,
    shippingDetailsFormValue,
    sizesFormValue,
    storeInfoFormValue,
  ]);

  const editPageHasUnsavedChangesRef = useRef(false);

  /** Track whether editable edit-page fields differ from the captured baseline. */
  const hasChanges = useMemo(() => {
    if (!isEditPage) {
      return true;
    }

    if (!isEditPageBaselineReady) {
      return false;
    }

    return hasStoreCheckoutEditChanges(editPageBaselineRef.current, {
      storeInfo: storeInfoFormValue as IStoreInfoFormShape,
      designStore: designStoreFormValue,
      sizes: sizesFormValue as ISizesFormValues,
      cartItems: editPageCartItems,
      shipping: {
        option: shippingDetailsFormValue.shippingOption ?? '',
        address: shippingDetailsFormValue,
      },
    });
  }, [
    designStoreFormValue,
    editPageCartItems,
    isEditPage,
    isEditPageBaselineReady,
    shippingDetailsFormValue,
    sizesFormValue,
    storeInfoFormValue,
  ]);

  editPageHasUnsavedChangesRef.current = isEditPage && isEditPageBaselineReady && hasChanges;

  const navigateToEditStoreOrderDetail = useCallback(() => {
    const resolvedOrderId =
      editStoreOrderId ?? window.location.pathname.match(/\/store\/edit\/([^/]+)/)?.[1];

    if (resolvedOrderId) {
      navigate(`/order/${resolvedOrderId}`, { replace: true });
    }
  }, [editStoreOrderId, navigate]);

  const handleEditStoreBackClick = useCallback(() => {
    navigateToEditStoreOrderDetail();
  }, [navigateToEditStoreOrderDetail]);

  const handleEditBrandingStateChange = useCallback(
    (branding: IStoreCustomizationBrandingState) => {
      editBrandingStateRef.current = branding;
    },
    []
  );

  const saveEditStoreChanges = useCallback(async () => {
    const baseline = editPageBaselineRef.current;
    if (!baseline || !effectiveStoreId) {
      return;
    }

    const isStoreInfoValid = await storeInfoForm.trigger(['storeName', 'storeDescription']);
    if (!isStoreInfoValid) {
      showToastrMessage('Please fix the errors in store info before saving.', 'failure');
      return;
    }

    const currentStoreInfo = storeInfoForm.getValues() as IStoreInfoFormShape;
    const currentDesignStore = designStoreForm.getValues();
    const currentSizes = sizesForm.getValues() as ISizesFormValues;
    const currentShippingValues = shippingDetailsForm.getValues();
    const currentShipping = {
      option: currentShippingValues.shippingOption ?? '',
      address: currentShippingValues,
    };
    const branding = buildBrandingStateForEditSave(
      currentDesignStore,
      savedStoreBranding,
      editBrandingStateRef.current
    );

    const payload = buildStoreEditSavePayload({
      orderId: Number(effectiveStoreId),
      baseline,
      current: {
        storeInfo: currentStoreInfo,
        designStore: currentDesignStore,
        sizes: currentSizes,
        branding,
        cartItems: editPageCartItems,
        shipping: currentShipping,
      },
    });

    if (!payload) {
      return;
    }

    try {
      await updateStore(payload).unwrap();

      captureStoreEdited(user?.email ?? '', Number(effectiveStoreId));

      const nextBaseline = buildStoreCheckoutEditBaseline({
        storeInfo: currentStoreInfo,
        designStore: currentDesignStore,
        sizes: currentSizes,
        cartItems: editPageCartItems,
        shipping: currentShipping,
      });

      editPageBaselineRef.current = nextBaseline;
      storeInfoForm.reset(nextBaseline.storeInfo);
      designStoreForm.reset(nextBaseline.designStore);
      sizesForm.reset(nextBaseline.sizes);
      showToastrMessage(STORE_EDIT_SAVE_SUCCESS_MESSAGE, 'success');
    } catch {
      showToastrMessage('Failed to save store changes. Please try again.', 'failure');
    }
  }, [
    designStoreForm,
    editPageCartItems,
    effectiveStoreId,
    savedStoreBranding,
    shippingDetailsForm,
    sizesForm,
    storeInfoForm,
    updateStore,
    user?.email,
  ]);

  const cachedCheckoutOrderId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('orderId');
  }, [location.search]);

  const isOnPaymentStep = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return showBraintreePaymentScreen || searchParams.get('step') === 'payment';
  }, [location.search, showBraintreePaymentScreen]);

  const currentStoreTypeValue = storeTypeForm.watch('storeType') || '';

  const hasNewStoreSetupProgress = useMemo(() => {
    if (isEditPage) {
      return false;
    }

    return hasNewStoreCheckoutSetupProgress({
      storeType: currentStoreTypeValue,
      defaultStoreType: defaultStoreTypeValue.storeType,
      storeInfo: storeInfoFormValue as IStoreInfoFormShape,
      defaultStoreInfo: defaultStoreInfoValue as IStoreInfoFormShape,
      designStore: designStoreFormValue as IDesignStoreFormShape,
      defaultDesignStore: defaultDesignStoreValue as IDesignStoreFormShape,
      sizes: sizesFormValue as ISizesFormValues,
      defaultSizes: defaultSizesValue,
      designGroups,
      donationAmounts,
      paymentOption: reviewOrderFormValue.paymentOption ?? '',
      defaultPaymentOption: defaultReviewOrderValue.paymentOption ?? '',
      shippingOption: shippingDetailsFormValue.shippingOption ?? '',
      defaultShippingOption: defaultShippingDetailsValue.shippingOption ?? '',
      shipTypeKey: shippingDetailsFormValue.shipTypeKey ?? '',
      defaultShipTypeKey: defaultShippingDetailsValue.shipTypeKey ?? '',
      orderName: orderNameFormValue.orderName ?? '',
      defaultOrderName: defaultOrderNameValue.orderName ?? '',
      hasCachedOrderIdInUrl: Boolean(cachedCheckoutOrderId),
      isOnPaymentStep,
    });
  }, [
    cachedCheckoutOrderId,
    currentStoreTypeValue,
    defaultDesignStoreValue,
    defaultOrderNameValue.orderName,
    defaultReviewOrderValue.paymentOption,
    defaultShippingDetailsValue.shipTypeKey,
    defaultShippingDetailsValue.shippingOption,
    defaultSizesValue,
    defaultStoreInfoValue,
    defaultStoreTypeValue.storeType,
    designGroups,
    designStoreFormValue,
    donationAmounts,
    isEditPage,
    isOnPaymentStep,
    orderNameFormValue.orderName,
    reviewOrderFormValue.paymentOption,
    shippingDetailsFormValue.shipTypeKey,
    shippingDetailsFormValue.shippingOption,
    sizesFormValue,
    storeInfoFormValue,
  ]);

  const executeNewStoreBackNavigation = useCallback(() => {
    dispatch(setCartOpened(false));

    if (isStoreCartDrawerViewport(viewportData.width)) {
      dispatch(setStoreCartOpened(true));
    } else {
      dispatch(setStoreCartOpened(false));
    }

    navigate(buildStoreCartOpenUrl(viewportData.width), { replace: true });
  }, [dispatch, navigate, viewportData.width]);

  const handleNewStoreBackClick = useCallback(() => {
    if (hasNewStoreSetupProgress && !orderProcessing) {
      setIsNewStoreLeaveModalOpen(true);
      return;
    }

    executeNewStoreBackNavigation();
  }, [executeNewStoreBackNavigation, hasNewStoreSetupProgress, orderProcessing]);

  const handleNewStoreLeaveModalClose = useCallback(() => {
    setIsNewStoreLeaveModalOpen(false);
  }, []);

  const handleNewStoreLeaveModalDiscard = useCallback(async () => {
    setIsNewStoreLeaveModalOpen(false);

    if (effectiveStoreId) {
      try {
        await deleteStore(effectiveStoreId).unwrap();
      } catch {
        showToastrMessage(STORE_MESSAGES.STORE_DELETE_FAILED, 'failure');
        return;
      }
    }

    executeNewStoreBackNavigation();
  }, [deleteStore, executeNewStoreBackNavigation, effectiveStoreId]);

  const storeCheckoutHeaderProps = useMemo(
    () =>
      isEditPage
        ? { onEditStoreBackClick: handleEditStoreBackClick }
        : { onNewStoreBackClick: handleNewStoreBackClick },
    [handleEditStoreBackClick, handleNewStoreBackClick, isEditPage]
  );

  const storeLaunchDisabledReason = useMemo(
    () =>
      getStoreLaunchDisabledReason({
        isEditPage,
        hasChanges,
        storeType: currentStoreTypeValue,
        storeName: storeInfoFormValue.storeName ?? '',
        storeDescription: storeInfoFormValue.storeDescription ?? '',
        orderUntil: storeInfoFormValue.orderUntil ?? '',
        designGroups,
        sizes: sizesFormValue as ISizesFormValues,
        shippingDetails: {
          shippingOption: shippingDetailsFormValue.shippingOption ?? '',
          shippingName: shippingDetailsFormValue.shippingName ?? '',
          street1: shippingDetailsFormValue.street1 ?? '',
          city: shippingDetailsFormValue.city ?? '',
          state: shippingDetailsFormValue.state,
          shippingZipCode: shippingDetailsFormValue.shippingZipCode ?? '',
        },
        donationAmounts,
      }),
    [
      isEditPage,
      hasChanges,
      currentStoreTypeValue,
      storeInfoFormValue.storeName,
      storeInfoFormValue.storeDescription,
      storeInfoFormValue.orderUntil,
      designGroups,
      sizesFormValue,
      donationAmounts,
      shippingDetailsFormValue.shippingOption,
      shippingDetailsFormValue.shippingName,
      shippingDetailsFormValue.street1,
      shippingDetailsFormValue.city,
      shippingDetailsFormValue.state,
      shippingDetailsFormValue.shippingZipCode,
    ]
  );

  const storeLaunchTooltipTitle = useMemo(
    () =>
      getStoreCheckoutLaunchTooltipTitle(
        storeLaunchDisabledReason === '',
        storeLaunchDisabledReason
      ),
    [storeLaunchDisabledReason]
  );

  const previewStoreId = useMemo(
    () => resolveStoreCheckoutPreviewStoreId(effectiveStoreId, editStoreOrderId),
    [editStoreOrderId, effectiveStoreId]
  );

  const canPreviewStore = Boolean(previewStoreId);

  const handlePreviewStore = useCallback(() => {
    openStorePreview(previewStoreId);
  }, [previewStoreId]);

  const { estimatedDeliveryRange: storeEstimatedDeliveryRange, timelineData: storeTimelineData } =
    useStoreCheckoutShippingTimeline({
      cartItems: cartItemsInfo,
      shipTypeKey: shippingDetailsFormValue.shipTypeKey ?? '',
      orderUntil: storeInfoFormValue.orderUntil ?? '',
      getTimeline,
    });

  const handleDeliveryShipTypeSelect = useCallback(
    (nextShipTypeKey: string) => {
      if (isFieldsDisabled) {
        return;
      }

      setShippingDetails('shipTypeKey', nextShipTypeKey);
    },
    [isFieldsDisabled, setShippingDetails]
  );

  useEffect(() => {
    if (
      shippingDetailsFormValue.shippingOption === 'one-place' &&
      !shippingDetailsFormValue.shipTypeKey
    ) {
      setShippingDetails('shipTypeKey', STORE_SHIPPING_DELIVERY_OPTION_TYPES.STANDARD);
    }
  }, [
    setShippingDetails,
    shippingDetailsFormValue.shipTypeKey,
    shippingDetailsFormValue.shippingOption,
  ]);

  /** Setting up step number based on store checkout step completion */
  useEffect(() => {
    const storeType = currentStoreTypeValue;
    const isStoreTypeComplete =
      !!storeType &&
      ['group-order', 'brand-drop', 'fundraiser', 'permanent-store'].includes(storeType);

    const storeName = (storeInfoFormValue.storeName ?? '').trim();
    const storeDescription = (storeInfoFormValue.storeDescription ?? '').trim();
    const orderUntil = storeInfoFormValue.orderUntil ?? '';
    const isPermanentStore = storeType === 'permanent-store';
    let isStoreInfoComplete = storeName !== '' && storeDescription !== '';
    if (!isPermanentStore) {
      isStoreInfoComplete = isStoreInfoComplete && orderUntil !== '';
    }

    const isPaymentComplete = !!(
      reviewOrderFormValue.paymentOption && reviewOrderFormValue.paymentOption !== ''
    );
    const isShippingComplete = !!shippingDetailsFormValue.shippingOption;
    const includePaymentStep =
      storeType !== 'permanent-store' &&
      showsStoreCheckoutPaymentOptionStep(
        uiVariationCtx?.uiVariation ?? STORE_CHECKOUT_UI_VARIATION_VALUES.PHASE_2
      );

    const stepConditions = [
      isStoreTypeComplete,
      isStoreInfoComplete,
      // Review step does not use step-completed; do not block progress on it
      true,
      isSizesValid || isSizesFormValid,
    ];
    if (includePaymentStep) {
      stepConditions.push(isPaymentComplete);
    }
    stepConditions.push(isShippingComplete);

    const firstIncompleteIndex = stepConditions.findIndex((isComplete) => !isComplete);
    const nextStepNumber =
      firstIncompleteIndex === -1 ? stepConditions.length - 1 : firstIncompleteIndex;

    const isPriceZero = isCartHasZeroPriceItem(currentFormValues as unknown as IRegularOrderForm);

    setCanPlaceOrder(storeLaunchDisabledReason === '');
    setPriceZeroCartItem(isPriceZero);
    setStepNumber(nextStepNumber);
  }, [
    currentStoreTypeValue,
    storeInfoFormValue.storeName,
    storeInfoFormValue.storeDescription,
    storeInfoFormValue.orderUntil,
    reviewOrderFormValue.paymentOption,
    shippingDetailsFormValue.shippingOption,
    isSizesValid,
    isSizesFormValid,
    uiVariationCtx?.uiVariation,
    currentFormValues,
    storeLaunchDisabledReason,
  ]);

  /** Setting up amount field for credit card validation */
  useEffect(() => {
    setValueCreditCard('amount', total.toString());
  }, [total, setValueCreditCard]);

  /** Updating steps data for moq when ship type is changed */
  const setUpStepsData = useCallback(
    (shipType: string) => {
      if (!itemShipPriceObj) return;
      if (cartItemsInfo?.length) {
        const updatedCartItems = cartItemsInfo.map((item: ICartItemDetail) => {
          const shipPriceObject = itemShipPriceObj[item.id]?.find(
            (itm: IAdditionalShippingPriceRes) => itm.type === shipType
          );

          if (shipPriceObject) {
            const selectedMinimum = Number(item.minQuant ?? item.quantity ?? 0);

            return {
              ...item,
              minQuant: item.isStore && selectedMinimum > 0 ? selectedMinimum : shipPriceObject.moq,
              shipType,
            };
          }
          return item;
        });
        setCartItemsInfo(updatedCartItems);
        dispatch(setStoreItems(updatedCartItems));
        const stepsData = prepareRegOrderStepsData(updatedCartItems, userOrg);
        setQuantitySizeItems(stepsData.quantitySizeItems);

        if (qtyCompRef.current && !isSizesValid) {
          const scrolledBlock = cartItemsInfo.length <= 2 ? 'center' : 'start';
          qtyCompRef.current.scrollIntoView({ behavior: 'smooth', block: scrolledBlock });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, itemShipPriceObj, userOrg, setCartItemsInfo, setQuantitySizeItems, isSizesValid]
  );

  /** Sets the suggested address in the form */
  const setSuggestedAddress = useCallback(
    (address: IAddress) => {
      const stateObj = {
        label: address.state,
        value: address.state,
      };

      setShippingDetails('street1', address.street1);
      setShippingDetails('street2', address.street2);
      setShippingDetails('city', address.city);
      setShippingDetails('state', stateObj);
      setShippingDetails('shippingZipCode', address.zipCode || '');
      setShippingDetails('suggestions', []);
      setShippingDetails('showSuggestions', false);

      dispatch(setCurrentShippingAddress(address));
      dispatch(setIsSuggestedAddressSelected(true));
      persistShippingAddress({
        ...shippingDetailsForm.getValues(),
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: stateObj,
        shippingZipCode: address.zipCode || '',
      });
      maybeTrackShippingInfoSectionCompleted();
    },
    [
      dispatch,
      maybeTrackShippingInfoSectionCompleted,
      persistShippingAddress,
      setShippingDetails,
      shippingDetailsForm,
    ]
  );

  useEffect(() => {
    if (shipTypeChanged) {
      setUpStepsData(shipTypeChanged);
    }
  }, [shipTypeChanged, setUpStepsData]);

  /**
   * Setting up component data and form
   *
   * setItemShipPriceObj - save the IAdditionalShippingPriceRes of each cart item, to change the moq
   * when ship type is changed, IAdditionalShippingPriceRes objects contain the moq for each shipping type
   *
   * setShipPriceObj - set the max of all cart items shipping objects est delivery date
   * setItemShipPriceObj - save the IAdditionalShippingPriceRes of each cart item, to change the moq
   * when ship type is changed, IAdditionalShippingPriceRes objects contain the moq for each shipping type
   *
   *  */
  const setUpComponentData = async () => {
    if (checkoutSourceItems.length && isCheckoutSourceReady) {
      setIsLoading(true);
      const { itemShipPriceObject, updatedCartItems } = await getUpdatedCartItemsRegOrder(
        checkoutSourceItems,
        getQuote,
        getTimeline
      );
      setIsLoading(false);
      setItemShipPriceObj(itemShipPriceObject);
      const cartItemsWithRejection = withDesignRejectionInfoList(updatedCartItems, userOrg);
      setCartItemsInfo(cartItemsWithRejection);
      dispatch(setStoreItems(cartItemsWithRejection));
      clearPersistedStoreCartDesignGroupMinimums();
      const stepsData = prepareRegOrderStepsData(cartItemsWithRejection, userOrg);
      setShipTypes(checkoutSourceItems);

      if (stepsData.reviewItems[0]?.name) {
        setValueOrderName('orderName', stepsData.reviewItems[0].name);
      }
      setReviewItems(stepsData.reviewItems);
      const notesObj: { [key: string]: string } = {};
      stepsData.reviewItems.forEach((itm) => {
        if (itm.item?.id) notesObj[itm.item?.id] = itm.item?.notes;
      });
      setRegOrderNotes(notesObj);
      setQuantitySizeItems(stepsData.quantitySizeItems);
      setCustomItem(stepsData.customizedDesigns);
      setIsOrderHasRejectedItems(isOrderHasLicensingRejectionItems(stepsData.reviewItems));
      triggerOrderName();
    }
  };

  /** Setting up data for components & form */
  useEffect(() => {
    if (!checkoutSourceItems.length || !isCheckoutSourceReady) {
      return;
    }

    if (lastSetupCheckoutSourceItemsKeyRef.current === checkoutSourceItemsKey) {
      return;
    }

    lastSetupCheckoutSourceItemsKeyRef.current = checkoutSourceItemsKey;
    setUpComponentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkoutSourceItemsKey,
    dispatch,
    setValueOrderName,
    triggerOrderName,
    setShipTypes,
    isCheckoutSourceReady,
    userOrg,
    checkoutSourceItems.length,
    setShippingDetails,
    setRegOrderNotes,
    setCartItemsInfo,
  ]);

  /** Strip legacy `step=order-details` from the URL; order details is the default checkout view. */
  useEffect(() => {
    if (isEditPage) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.get('step') === 'order-details') {
      updateCheckoutUrlParams({ step: null }, true);
    }
  }, [isEditPage, updateCheckoutUrlParams]);

  /** Populate cached checkout data from Redis when Braintree payment flow is enabled. */
  useEffect(() => {
    // Skip on edit page - uses static prefilled data
    if (isEditPage) return;

    const fetchOrderDataFromRedis = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const step = searchParams.get('step');
      const orderId = searchParams.get('orderId');

      if (step === 'payment') {
        setShowBraintreePaymentScreen(true);
      }

      if (orderId) {
        try {
          const redisOrderResponse = await getCachedOrder(orderId);

          if (redisOrderResponse && redisOrderResponse.data) {
            const redisOrderData = redisOrderResponse.data;

            if (redisOrderData.storeTypeForm) {
              storeTypeForm.reset(redisOrderData.storeTypeForm);
            }
            if (redisOrderData.storeInfoForm) {
              storeInfoForm.reset(redisOrderData.storeInfoForm);
            }
            if (redisOrderData.designStoreForm) {
              designStoreForm.reset(redisOrderData.designStoreForm);
            }
            if (redisOrderData.orderNameForm) {
              orderNameForm.reset(redisOrderData.orderNameForm);
            }
            if (redisOrderData.reviewOrderForm) {
              reviewOrderForm.reset(redisOrderData.reviewOrderForm);
            }
            if (redisOrderData.sizesForm) {
              sizesForm.reset(redisOrderData.sizesForm);
            }
            if (redisOrderData.shippingDetailsForm) {
              shippingDetailsForm.reset(redisOrderData.shippingDetailsForm);

              setShippingDetails('shipTypeKey', redisOrderData.shippingDetailsForm.shipTypeKey);
              setShippingDetails('shippingCost', redisOrderData.shippingDetailsForm.shippingCost);
            }
            if (redisOrderData.creditCardForm) {
              creditCardForm.reset(redisOrderData.creditCardForm);
            }
            if (redisOrderData.payLaterForm) {
              payLaterForm.reset(redisOrderData.payLaterForm);
            }
            if (redisOrderData.universityApprovalForm) {
              universityApprovalForm.reset(redisOrderData.universityApprovalForm);
            }
            if (redisOrderData.summaryForm) {
              summaryForm.reset(redisOrderData.summaryForm);
            }
          }

          if (!redisOrderResponse || redisOrderResponse?.error) {
            updateCheckoutUrlParams({ orderId: null, step: null });
            setShowBraintreePaymentScreen(false);
          }
        } catch (error) {
          updateCheckoutUrlParams({ orderId: null, step: null });
          setShowBraintreePaymentScreen(false);
          console.error('Failed to fetch order data from Redis:', error);
        }
      }
    };

    /** Only trigger if `isBraintreeEnabled` is true */
    if (isBraintreeEnabled) {
      fetchOrderDataFromRedis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBraintreeEnabled, isEditPage]);

  useEffect(() => {
    if (
      hasStoreIdInUrl ||
      isEditPage ||
      !isBraintreeEnabled ||
      isCartInfoLoading ||
      !(isCartFetchedSuccess || isCartFetchedError) ||
      storeCartApiItems.length > 0 ||
      (cartItemsInfo?.length ?? 0) > 0 ||
      (storeItems?.length ?? 0) > 0
    ) {
      return;
    }

    updateCheckoutUrlParams({ step: null, orderId: null });
  }, [
    cartItemsInfo,
    hasStoreIdInUrl,
    isBraintreeEnabled,
    isCartFetchedSuccess,
    isCartFetchedError,
    isCartInfoLoading,
    isEditPage,
    storeCartApiItems,
    storeItems,
    updateCheckoutUrlParams,
  ]);

  /**
   * Hook to set the suggestions and showSuggestions in the shipping details form after validating the address.
   */
  useEffect(() => {
    setShippingDetails('suggestions', suggestions);
    setShippingDetails('showSuggestions', showSuggestions);
  }, [setShippingDetails, showSuggestions, suggestions]);

  /** Function to get the s3S signed url for tax exempt certificate. */
  const getTaxExemptS3SignedUrl = async (file: File) => {
    const fileType = file.type;
    const formData = new FormData();

    formData.append('fileName', file.name);
    formData.append('fileType', fileType);
    formData.append('folder', 'comments');
    formData.append('filedata', file);

    /** Get the pre-signed URL from the server. */
    return getS3PreSignedUrl(formData).unwrap();
  };

  const getOrderIdFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('orderId');
  };

  const getGuaranteedValue = () => summaryFormValue.guarantee !== '0';

  /**
   * Validates the address if required.
   */
  const validateAddressIfNeeded = async () => {
    if (
      (addressValidationState.addressValidationCount === 0 &&
        !addressValidationState.isShippingFormModified) ||
      (addressValidationState.addressValidationCount === 0 &&
        addressValidationState.isShippingFormModified)
    ) {
      setValidatingAddress(true);
      const isValid = await validateAddressInfo(shippingDetailsFormValue as ShippingInformation);
      setValidatingAddress(false);
      return isValid;
    }
    return addressValidationState.addressValidationCount === 1;
  };

  /** Launches the store via POST /api/store/:storeId/launch */
  const launchStoreCheckout = async (): Promise<void> => {
    if (!effectiveStoreId) {
      return;
    }

    setOrderProcessing(true);
    setIsOrderAPIFailed(null);
    setShowMobileSummaryOverlay(false);

    const isIndividuallyShipped = shippingDetailsFormValue.shippingOption === 'direct';
    const canProceed = isIndividuallyShipped ? true : await validateAddressIfNeeded();

    if (!canProceed) {
      setOrderProcessing(false);
      return;
    }

    try {
      const payload = buildStoreLaunchPayload({
        storeId: Number(effectiveStoreId),
        orderUntil: storeInfoFormValue.orderUntil ?? '',
        shipTypeKey: shippingDetailsFormValue.shipTypeKey ?? '',
        timelineData: storeTimelineData,
        viewport: screen,
      });

      await launchStore({ storeId: effectiveStoreId, payload }).unwrap();

      captureStoresSetupCompleted(user?.email ?? '', Number(effectiveStoreId));

      showToastrMessage(STORE_MESSAGES.LAUNCH_SUCCESS, 'success');
      await resetStoreCartItems(resolveStoreCartApiItemIds(storeCartApiItems));
      navigate(getPostStoreLaunchPath(effectiveStoreId, isStoreInvitesEnabled));
    } catch {
      showToastrMessage(STORE_MESSAGES.LAUNCH_FAILED, 'failure');
      setIsOrderAPIFailed({
        message: STORE_MESSAGES.LAUNCH_FAILED,
        category: '',
      });
    } finally {
      setOrderProcessing(false);
    }
  };

  /** Function to place an order */
  const createOrder = async () => {
    if (isEditPage) {
      setOrderProcessing(true);
      try {
        await saveEditStoreChanges();
      } finally {
        setOrderProcessing(false);
      }
      return;
    }

    if (effectiveStoreId) {
      await launchStoreCheckout();
      return;
    }

    /** Check to validate promo code just before order creation */
    if (+summaryFormValue.subTotal > 0 && promoApplied) {
      const latestPromoCodeUser = (await refetchPromoCodeUser()).data;

      if (
        latestPromoCodeUser &&
        (latestPromoCodeUser.promoCode as PromoCodeValidateError).message
      ) {
        if (showMobileSummary) {
          setShowMobileSummaryOverlay(true);
        }
        setPromoCodeError(PROMO_CODE_ERROR_MSG);
        return;
      }
    }

    setOrderProcessing(true);
    setIsOrderAPIFailed(null);
    setShowMobileSummaryOverlay(false);

    let addressValidated;

    /**
     * If the address is not validated, then validate the address.
     */
    if (
      (addressValidationState.addressValidationCount === 0 &&
        !addressValidationState.isShippingFormModified) ||
      (addressValidationState.addressValidationCount === 0 &&
        addressValidationState.isShippingFormModified)
    ) {
      addressValidated = await validateAddressInfo(shippingDetailsFormValue as ShippingInformation);
    }

    /**
     * we need not revalidate the address if the client picked one from the popup.
     * The objective is to ensure the client picked a valid address so that the order can shipped.
     * addressValidationCount is count of how many times the client has tried to validate the address.
     * if the client has tried to validate the address more than once, we can assume that the client has picked a valid address.
     */

    /** If the BRAINTREE FLAG is enabled, then proceed to the order creation by skipping the address validation here. */
    const proceedOrderCreation =
      isBraintreeEnabled && !isPayLater
        ? true
        : addressValidated || addressValidationState.addressValidationCount === 1;

    if (proceedOrderCreation) {
      const attachments = [];

      if (salesTaxCert) {
        const signedUrlData = await getTaxExemptS3SignedUrl(salesTaxCert);
        const fileName = await uploadTaxExemptFileToS3(signedUrlData, salesTaxCert);

        if (fileName) {
          attachments.push({
            name: 'taxExemptFile',
            fileName,
          });
        }
      }

      const regOrderPayLoad = getRegularOrderPayload(
        currentFormValues as unknown as IRegularOrderForm,
        quantitySizeItems,
        Boolean(simulateError),
        showCollegiateInfoForm,
        isPayLater,
        regOrderNotes,
        getNewTimelineShippingRanges(
          cartItemsInfo?.length > 0 ? cartItemsInfo[0]?.timelineData ?? [] : [],
          shipTypeChanged ?? 'standard',
          false,
          getLatestRestockDate(activeItemRestockDate ?? {}),
          false,
          true,
          !getGuaranteedValue(),
          timelineChangesEnabled
        ),
        getNewTimelineShippingRanges(
          cartItemsInfo?.length > 0 ? cartItemsInfo[0]?.timelineData ?? [] : [],
          shipTypeChanged ?? 'standard',
          false,
          getLatestRestockDate(activeItemRestockDate ?? {}),
          false,
          true,
          false,
          timelineChangesEnabled
        ),
        getNewTimelineShippingRanges(
          cartItemsInfo?.length > 0 ? cartItemsInfo[0]?.timelineData ?? [] : [],
          shipTypeChanged ?? 'standard',
          false,
          getLatestRestockDate(activeItemRestockDate ?? {}),
          false,
          true,
          true,
          timelineChangesEnabled
        )
      );

      let reqPayload = {
        ...regOrderPayLoad,
        isBraintreePayment: isBraintreeEnabled,
        attachments,
        cachedOrderId: getOrderIdFromUrl(),
        ...(promoApplied && {
          promoCodeId: promoCodeData?.id,
          promoCodeDiscount: Number(promoCodeData?.discount),
          promoCode: promoCodeData?.code,
          promoCodeUserId: promoCodeUser?.promoCodeUser.id,
        }),
      };

      if (!isPayLater && isBraintreeEnabled) {
        try {
          const paymentNoncePayload = selectedSavedMethod ? null : await requestPaymentMethod();
          const paymentMethodNonce = selectedSavedMethod
            ? await getSavedPaymentNonce()
            : paymentNoncePayload?.nonce;
          const isDuplicateNewMethod =
            paymentNoncePayload != null &&
            isPaymentMethodPayloadAlreadySaved(paymentNoncePayload, savedMethods);
          const shouldStoreInVault =
            selectedSavedMethod == null && Boolean(consentGiven) && !isDuplicateNewMethod;
          const paymentMethodDetails = selectedSavedMethod
            ? getSavedPaymentMethodDetails(selectedSavedMethod)
            : getCheckoutPaymentMethodDetails(
                paymentNoncePayload as NonNullable<typeof paymentNoncePayload>,
                shouldStoreInVault
              );

          const paymentPayload = {
            amount: summaryFormValue.total,
            paymentMethodNonce,
            deviceData: paymentNoncePayload?.deviceData,
            paymentMethodDetails,
            shouldStoreInVault,
            utmState,
          };

          reqPayload = {
            ...reqPayload,
            ...paymentPayload,
          };
        } catch (error) {
          if (selectedSavedMethod) {
            const isSavedMethodUnavailable = error instanceof SavedPaymentMethodUnavailableError;
            if (isSavedMethodUnavailable) {
              removeUnavailableSavedMethod(selectedSavedMethod.token);
              refetchSavedMethods().catch(() => {
                // Keep checkout usable if refreshing the display-only list fails.
              });
            }
            setIsOrderAPIFailed({
              message: isSavedMethodUnavailable ? SAVED_PM_UNAVAILABLE_MSG : SAVED_PM_RETRY_MSG,
              category: 'payment',
            });
          }
          setOrderProcessing(false);
          return;
        }
      }

      const response: ISuccess | IError = await createRegularOrder(reqPayload);

      if ('data' in response) {
        showToastrMessage('Woohoo! Your Order is in.', 'success');

        /**
         * Mixpanel event to capture the checkout completed event.
         */
        const productIds = cartItemsInfo
          .map((item: ICartItemDetail) => item.apparelStyleCode)
          .join(';');

        if (response.data && 'id' in response.data) {
          captureCheckoutCompleted(
            response.data.id,
            productIds,
            summaryFormValue.subTotal,
            summaryFormValue.total,
            summaryFormValue.shipping,
            response.data.created_by,
            response.data.created_by_id,
            summaryFormValue.shipTypeKey,
            getGuaranteedValue(),
            resolveCheckoutCompletedPaymentMethod(
              reviewOrderFormValue.paymentOption === 'invoice',
              selectedSavedMethod
                ? getSavedPaymentMethodAnalyticsName(selectedSavedMethod.type)
                : selectedPaymentMethod
            ),
            selectedSavedMethod !== null
          );
        }

        await resetCartItems();

        dispatch(promoCodeUserQuery.util.resetApiState());
        dispatch(promoCodeQuery.util.resetApiState());
        dispatch(setConsentGiven(false));
        await refetchSavedMethods();
        navigate('/');
        setOrderProcessing(false);
      }

      if ('error' in response) {
        const error = response.error as IError;
        const failureMessage = error.data?.message ?? '';
        const savedMethodUnavailable =
          selectedSavedMethod != null && isSavedPaymentMethodUnavailableError(failureMessage);
        if (savedMethodUnavailable) {
          removeUnavailableSavedMethod(selectedSavedMethod.token);
          refetchSavedMethods().catch(() => {
            // Keep checkout usable if refreshing the display-only list fails.
          });
        }
        setOrderProcessing(false);
        setIsOrderAPIFailed({
          message: savedMethodUnavailable ? SAVED_PM_UNAVAILABLE_MSG : failureMessage,
          category: error.data?.category ?? '',
        });
      }
      dispatch(resetAddressValidationCount());
      dispatch(setIsSuggestedAddressSelected(false));
      dispatch(resetCurrentShippingAddress());
      dispatch(setIsShippingFormModified(false));
    } else {
      setOrderProcessing(false);
    }
  };

  /** Method to render custom icons */
  const customIcon = (source: string, className: string) => (
    <Icon source={source} className={className} />
  );

  /** Tick mark icon for steps */
  const tickIcon = () => <Icon source="/src/assets/svg/check-simple.svg" className="icon--check" />;

  /**
   * Method to reload the page
   */
  const reloadThePage = () => {
    setIsOrderAPIFailed(null);

    /** If the BRAINTREE FLAG is enabled, then show the payment step. */
    if (isBraintreeEnabled) {
      const searchParams = new URLSearchParams(window.location.search);
      const orderId = searchParams.get('orderId');
      updateCheckoutUrlParams({ orderId, step: 'payment' });
    } else {
      navigate(`/store-checkout/new`, { replace: true });
    }
  };

  /** Resetting all promo code related data values */
  const resetPromoCodeData = () => {
    setPromoCodeData(undefined);
    setOrderSummary('promoCode', '');
    setPromoApplied(false);
    setPromoCodeError('');
  };

  /** Clicking on x button inside promo code input in order summary */
  const onPromoRemoveClick = async () => {
    if (!promoCodeUser) return;

    const response = await updatePromoCodeUser({
      promoCodeUserId: promoCodeUser?.promoCodeUser.id,
      data: { status: 0 },
    });

    if ('error' in response) {
      showToastrMessage('Failed to remove the promo code.', 'failure');
    }

    if ('data' in response) {
      resetPromoCodeData();
    }
  };

  /**
   * Getting the promo code discount if active promo code user
   * record exists - to persist applied promo code
   *
   * Setting the promo code discount value inside
   */
  useEffect(() => {
    if (promoCodeUser && summaryFormValue.subTotal) {
      const promoCodeDetails = promoCodeUser.promoCode as PromoCodeData;
      if (promoCodeUser.promoCode && !promoCodeDetails.message) {
        const { discount } = promoCodeDetails;
        setPromoCodeData({ ...promoCodeDetails, discount });
        setOrderSummary('promoCode', promoCodeDetails.code);
        setPromoApplied(true);
        setPromoCodeError('');
      } else if (promoCodeUser.promoCode && promoCodeDetails.message) {
        setCartDetails('promoCode', promoCodeDetails.code);
        setOrderSummary('promoCode', promoCodeDetails.code);
        if (promoCodeDetails) setPromoCodeData({ ...promoCodeDetails, discount: '' });
        setPromoApplied(true);
        setPromoCodeError(promoCodeDetails.message);
      }
    }
  }, [promoCodeUser, summaryFormValue.subTotal, setOrderSummary, setCartDetails]);

  /** On Promo apply click, show visual confetti animation */
  const showConfettiAnimation = () => {
    setShowConfetti(true);

    setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
  };

  /** On Promo apply click, set the promo code data and promo code user data */
  const promoCodeApply = async () => {
    const value = summaryFormValue.promoCode;

    if (!value) return;

    if (value) {
      const promoCodeUserRes = await postPromoCodeUser({ promo_code: value });

      if ('error' in promoCodeUserRes) {
        setPromoCodeError(PROMO_CODE_ERROR_MSG);
      } else if (promoCodeUserRes.data.promoCode) {
        const promoCodeDetails = promoCodeUserRes.data.promoCode as PromoCodeData;
        const { discount } = promoCodeDetails;
        setPromoCodeError('');
        setPromoCodeData({ ...promoCodeDetails, discount });
        setPromoApplied(true);
        if (isMobile) {
          setMobPromoAppliedOverlay(true);
        }
        showConfettiAnimation();
      } else if (promoCodeUserRes.data.message) {
        setPromoCodeError(promoCodeUserRes.data.message);
      } else {
        setPromoCodeError(PROMO_CODE_ERROR_MSG);
      }
    }
  };

  /** Clicking on apply button inside promo code input in order summary */
  const onPromoApplyClick = () => {
    promoCodeApply();
  };

  /* Close the mobile promo applied overlay */
  const onMobPromoClose = () => {
    setMobPromoAppliedOverlay(false);
  };

  /**
   * Prepares the checkout form data to be cached.
   */
  const prepareCheckoutFormData = () => ({
    orderInfo: {
      storeTypeForm: storeTypeForm.getValues(),
      storeInfoForm: storeInfoForm.getValues(),
      designStoreForm: designStoreForm.getValues(),
      orderNameForm: orderNameForm.getValues(),
      reviewOrderForm: reviewOrderForm.getValues(),
      sizesForm: sizesForm.getValues() as unknown as { sizes: Record<string, unknown> },
      shippingDetailsForm: shippingDetailsForm.getValues(),
      creditCardForm: creditCardForm.getValues(),
      payLaterForm: payLaterForm.getValues(),
      universityApprovalForm: universityApprovalForm.getValues(),
      summaryForm: summaryForm.getValues(),
    },
  });

  /**
   * Handles caching the order data and navigating to the payment step.
   */
  const handleCacheStorageAndNavigate = async (checkoutFormData: {
    orderInfo: ICheckoutFormData;
  }) => {
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get('orderId');

    try {
      if (orderId) {
        await updateOrderInRedisCache({ orderId, checkoutFormData });
      } else {
        const response = await createOrderInRedisCache(checkoutFormData);

        if ('data' in response) {
          const { uniqueId } = response.data;
          updateCheckoutUrlParams({ orderId: uniqueId, step: 'payment' });
          return;
        }
      }
    } catch (error) {
      console.error('Failed to update Redis cache:', error);
    }

    /** Ensure navigation to payment step even if caching fails */
    updateCheckoutUrlParams({ orderId: orderId ?? null, step: 'payment' });
  };

  /**
   * Scrolls to the top of the page.
   */
  const scrollToTop = () => {
    const element = document.getElementById('store-checkout-id');
    if (element) {
      element.scrollTop = 0;
    }
  };

  /**
   * Method used to validate the order address and shows the braintree payment DROP In UI.
   */
  const continueToPay = async () => {
    if (isEditPage) {
      setOrderProcessing(true);
      try {
        await saveEditStoreChanges();
      } finally {
        setOrderProcessing(false);
      }
      return;
    }

    if (effectiveStoreId) {
      await launchStoreCheckout();
      return;
    }

    if (await validateAddressIfNeeded()) {
      const checkoutFormData = prepareCheckoutFormData();
      await handleCacheStorageAndNavigate(checkoutFormData);
      scrollToTop();
      setShowBraintreePaymentScreen(true);
    } else {
      setValidatingAddress(false);
    }
  };

  /** Method to handle the order info view */
  const handleOrderInfoView = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get('orderId');

    if (orderId) {
      updateCheckoutUrlParams({ orderId, step: null });
    }

    setShowBraintreePaymentScreen(false);
  };

  const handleConsentChange = useCallback(
    (checked: boolean) => {
      dispatch(setConsentGiven(checked));
    },
    [dispatch]
  );

  const handleSelectSavedPaymentMethod = useCallback(
    (token: string) => {
      selectSavedMethod(token);
      dispatch(setConsentGiven(false));
      setIsOrderAPIFailed(null);
    },
    [dispatch, selectSavedMethod]
  );

  const handleNewPaymentMethodInteraction = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>) => {
      if (!isUsingSavedPaymentMethod) return;
      if (!(event.target instanceof Element) || !event.target.closest('.hq-braintree-dropin')) {
        return;
      }
      selectNewPaymentMethod();
      setIsOrderAPIFailed(null);
    },
    [isUsingSavedPaymentMethod, selectNewPaymentMethod]
  );

  const hasStoreCartData = useMemo(
    () =>
      (cartItemsInfo?.length ?? 0) > 0 ||
      (storeItems?.length ?? 0) > 0 ||
      storeCartApiItems.length > 0 ||
      mergedStoreCheckoutItems.length > 0 ||
      storeDetailsCartItems.length > 0,
    [cartItemsInfo, mergedStoreCheckoutItems, storeCartApiItems, storeDetailsCartItems, storeItems]
  );

  const isSalesUser = Boolean(
    (
      user as typeof user & {
        permissions?: Record<string, { deal_mandatory?: boolean } | undefined>;
      }
    )?.permissions?.Orders?.deal_mandatory
  );

  const isStoreCartResolved = isCheckoutSourceResolved;
  const isHydratingCart =
    !hasStoreIdInUrl && storeCartApiItems.length > 0 && (cartItemsInfo?.length ?? 0) === 0;
  const isWaitingForStoreDetails =
    hasStoreIdInUrl &&
    !isStoreDetailsHydrated &&
    (isStoreDetailsLoading || (!resolvedStoreDetails && isStoreDetailsFetching));
  const isWaitingForEditPageHydration =
    isEditPage && hasStoreIdInUrl && Boolean(resolvedStoreDetails) && !isStoreDetailsHydrated;
  const isWaitingForStoreCartMerge =
    hasStoreIdInUrl &&
    !isEditPage &&
    isStoreDetailsFetchSettled &&
    hasStoreDetailOrderItems &&
    cartItemsForStoreMerge.length === 0 &&
    (isCartInfoLoading || !isCartFetchedSuccess);
  const isHydratingStoreCart =
    hasStoreIdInUrl &&
    !isEditPage &&
    isCheckoutSourceReady &&
    checkoutSourceItems.length > 0 &&
    (cartItemsInfo?.length ?? 0) === 0;
  const isHydratingStoreDetailsForms =
    hasStoreIdInUrl && !isEditPage && resolvedStoreDetails != null && !isStoreDetailsHydrated;
  const isWaitingForStoreOrCartData = isWaitingForStoreDetails || isWaitingForStoreCartMerge;

  const isStoreTypeStepComplete =
    currentStoreTypeValue !== '' &&
    ['group-order', 'brand-drop', 'fundraiser', 'permanent-store'].includes(currentStoreTypeValue);
  const storeCheckoutProgressStep = isStoreTypeStepComplete ? 1 : 0;

  if (
    isWaitingForStoreOrCartData ||
    isWaitingForEditPageHydration ||
    isHydratingCart ||
    isHydratingStoreCart ||
    isHydratingStoreDetailsForms
  ) {
    return (
      <div className={styleClass}>
        <Spinner tip={STORE_CHECKOUT_LOADING_TIP} size="default" />
      </div>
    );
  }

  if (hasStoreIdInUrl && isStoreDetailsFetchSettled && !resolvedStoreDetails) {
    return (
      <div className={styleClass}>
        <Result
          primaryText="Unable to load store"
          secondarytText="We couldn't fetch this store. Please try again."
          showTryAgainButton
          onTryAgainClicked={reloadThePage}
        />
      </div>
    );
  }

  if (
    isStoreCartResolved &&
    !(hasStoreIdInUrl ? isStoreDetailsLoading : isCartInfoLoading) &&
    !hasStoreCartData
  ) {
    return (
      <div className={styleClass}>
        <Result primaryText="Empty cart" secondarytText="Add items to continue" />
      </div>
    );
  }

  if (isOrderAPIFailed) {
    return (
      <div className={styleClass}>
        {isOrderAPIFailed.category === 'payment' ? (
          <Result
            primaryText="Oh fish! Something went a bit wonky. Can you try again?"
            secondarytText="Don't worry, no money has been deducted from your account."
            icon="/src/assets/svg/order_api_fail.svg"
            showTryAgainButton
            onTryAgainClicked={reloadThePage}
          />
        ) : (
          <Result
            primaryText="Oops!"
            secondarytText="Don't worry! The internet trips like that sometimes."
            icon="/src/assets/svg/order_api_fail.svg"
            showTryAgainButton
            onTryAgainClicked={reloadThePage}
          />
        )}
      </div>
    );
  }

  /** Steps Information */
  const currentStoreType = storeTypeForm.watch('storeType');
  const checkoutUiVariation =
    uiVariationCtx?.uiVariation ?? STORE_CHECKOUT_UI_VARIATION_VALUES.PHASE_2;
  const showPaymentOptionStep =
    currentStoreType !== 'permanent-store' &&
    showsStoreCheckoutPaymentOptionStep(checkoutUiVariation);

  const paymentOptionStep: StepProgressItem = {
    title: (
      <div>
        <div className={`${styleClass}__step-title`}>Select Payment Option</div>
        <FormProvider {...reviewOrderForm}>
          <SelectPaymentOption storeType={storeTypeForm.watch('storeType') || undefined} />
        </FormProvider>
      </div>
    ),
    icon: tickIcon(),
    className: (() => {
      const paymentOption = reviewOrderForm.watch('paymentOption');
      const completed = paymentOption && paymentOption !== '' ? 'step-completed' : '';
      return completed || '';
    })(),
  };

  const stepArray: StepProgressItem[] = [
    {
      title: (
        <div className={isEditPage ? 'step-disabled' : ''}>
          <div className={`${styleClass}__step-header`}>
            <div className={`${styleClass}__step-title`}>
              {STORE_CHECKOUT_STEP_LABELS.PICK_STORE_TYPE}
            </div>
            {isEditPage && (
              <div className={`${styleClass}__step-banner`}>
                <span className={`${styleClass}__step-banner-text`}>
                  {STORE_EDIT_STORE_TYPE_LOCKED_BANNER}
                </span>
                <button
                  type="button"
                  className={`${styleClass}__step-banner-icon-button`}
                  onClick={() => window.open(`mailto:${STORE_EDIT_SUPPORT_EMAIL}`, '_blank')}
                  aria-label="Contact support via email"
                >
                  <Icon source={STORE_EDIT_BANNER_MAIL_ICON_SRC} width="16px" height="16px" />
                </button>
              </div>
            )}
          </div>
          <FormProvider {...storeTypeForm}>
            <PickStoreType />
          </FormProvider>
        </div>
      ),
      icon: tickIcon(),
      className: (() => {
        const storeType = storeTypeForm.watch('storeType');
        // Only mark as completed if storeType exists and is a valid option
        const completed =
          storeType &&
          ['group-order', 'brand-drop', 'fundraiser', 'permanent-store'].includes(storeType)
            ? 'step-completed'
            : '';
        return `${completed} ${isEditPage ? 'step-disabled' : ''}`.trim();
      })(),
    },
    {
      title: (
        <div>
          <div className={`${styleClass}__step-title`}>
            {STORE_CHECKOUT_STEP_LABELS.ADD_STORE_INFO}
          </div>
          <FormProvider {...storeInfoForm}>
            <StoreDetails
              storeType={storeTypeForm.watch('storeType')}
              userName={user?.full_name ?? ''}
              isReadOnly={isStoreInfoReadOnly}
              isEditStorePage={isEditPage}
              designForm={designStoreForm}
              savedBranding={savedStoreBranding}
              onBrandingStateChange={handleEditBrandingStateChange}
            />
          </FormProvider>
        </div>
      ),
      icon: tickIcon(),
      className: (() => {
        const storeName = storeInfoForm.watch('storeName');
        const orderUntil = storeInfoForm.watch('orderUntil');
        const storeDescription = storeInfoForm.watch('storeDescription');
        const storeType = storeTypeForm.watch('storeType');
        const isPermanentStore = storeType === 'permanent-store';

        let isComplete = !!(
          storeName &&
          storeName.trim() !== '' &&
          storeDescription &&
          storeDescription.trim() !== ''
        );

        if (!isPermanentStore) {
          isComplete = isComplete && !!(orderUntil && orderUntil !== '');
        }

        return isComplete ? 'step-completed' : '';
      })(),
    },
    {
      title: (
        <div>
          <div className={`${styleClass}__step-title`}>Review and Confirm Order Items</div>
          <FormProvider {...reviewOrderForm}>
            <StoreReviewOrderStep
              items={cartItemsInfo.length > 0 ? cartItemsInfo : storeItems}
              moqShippingLane={moqShippingLane}
              onShippingLaneRequoteComplete={handleShippingLaneRequoteComplete}
              onDesignGroupsChange={setDesignGroups}
              donationAmounts={donationAmounts}
              onDonationAmountsChange={setDonationAmounts}
              onItemsChange={(nextItems) => {
                setCartItemsInfo(nextItems);
                dispatch(setStoreItems(nextItems));
              }}
              storeId={effectiveStoreId}
              storeType={storeTypeForm.watch('storeType') || undefined}
              charityName={storeInfoFormValue.charityName}
              isReadOnly={isReviewOrderStepLocked}
              isItemTitleEditable={isEditPage}
              isPricingReadOnly={isReviewOrderStepLocked}
            />
          </FormProvider>
        </div>
      ),
      icon: tickIcon(),
      className: '',
    },
    {
      title: (
        <div className={isConfirmSizesReadOnly ? 'step-disabled' : ''}>
          <div className={`${styleClass}__step-header`}>
            <div className={`${styleClass}__step-title`}>
              {STORE_CHECKOUT_STEP_LABELS.CONFIRM_SIZES}
            </div>
            {isConfirmSizesReadOnly && (
              <div className={`${styleClass}__step-banner`}>
                <span className={`${styleClass}__step-banner-text`}>
                  You cannot change sizes in a live store. This store has already started receiving
                  orders.
                </span>
              </div>
            )}
          </div>
          <FormProvider {...sizesForm}>
            <StoreConfirmSizes
              designGroups={designGroups}
              sizesForm={sizesForm as UseFormReturn<ISizesFormValues>}
              isReadOnly={isConfirmSizesReadOnly}
              isEditStorePage={isEditPage}
              sizeOrderCounts={storeSizeOrderCounts}
              isSalesUser={isSalesUser}
              mobile={isMobile}
              storeId={effectiveStoreId}
              onValidityChange={setIsSizesFormValid}
            />
          </FormProvider>
        </div>
      ),
      icon: tickIcon(),
      className: (() => {
        const completed = isSizesValid || isSizesFormValid ? 'step-completed' : '';
        return `${completed} ${isConfirmSizesReadOnly ? 'step-disabled' : ''}`.trim();
      })(),
    },
    ...(showPaymentOptionStep ? [paymentOptionStep] : []),
    {
      title: (
        <div>
          <div className={`${styleClass}__step-title`}>Add Shipping Info</div>
          <FormProvider {...shippingDetailsForm}>
            <div className={shippingWrapperClass}>
              <div className={`${shippingWrapperClass}__wrapper`}>
                <div className={`${styleClass}__section-spacing`}>
                  <SelectShippingOption
                    hasMtoItems={hasMtoItems}
                    onUserShippingOptionChange={handleUserShippingOptionChangeWithTracking}
                  />
                </div>

                {shippingDetailsFormValue.shippingOption === 'one-place' && (
                  <div className={`${styleClass}__section-spacing`}>
                    <div className={`${styleClass}__delivery-row`}>
                      <div className={`${styleClass}__delivery-label`}>
                        {STORE_SHIPPING_TO_LABEL}
                      </div>
                      <div className={`${styleClass}__delivery-actions`}>
                        {STORE_CHECKOUT_DELIVERY_SHIP_TYPE_OPTIONS.map((option) => {
                          const isSelected =
                            shippingDetailsFormValue.shipTypeKey === option.shipTypeKey;

                          return (
                            <button
                              key={option.shipTypeKey}
                              type="button"
                              className={`${styleClass}__delivery-badge ${
                                option.shipTypeKey === STORE_SHIPPING_DELIVERY_OPTION_TYPES.STANDARD
                                  ? `${styleClass}__delivery-badge--standard`
                                  : `${styleClass}__delivery-badge--free`
                              } ${isSelected ? `${styleClass}__delivery-badge--selected` : ''}`}
                              onClick={() => handleDeliveryShipTypeSelect(option.shipTypeKey)}
                              disabled={isReviewOrderStepLocked}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {shippingDetailsFormValue.shippingOption === 'one-place' && (
                  <Suspense fallback={<div>Loading...</div>}>
                    <StoreRecentAddress
                      recentAddresses={recentAddressSuggestions}
                      selectedAddressId={selectedAddressId}
                      onAddressSelect={handleRecentAddressSelect}
                      isReadOnly={isBulkShippingAddressReadOnly}
                    />
                    <StoreShippingFormTwoColumn
                      onShippingFieldChange={handleShippingAddressFieldChange}
                      isReadOnly={isBulkShippingAddressReadOnly}
                    />
                    {shippingDetailsFormValue.showSuggestions &&
                      shippingDetailsFormValue.suggestions && (
                        <AddressSuggestion
                          suggestedAddress={shippingDetailsFormValue.suggestions}
                          onSuggestedAddressClick={setSuggestedAddress}
                          isAddressModalOpen={shippingDetailsFormValue.showSuggestions}
                          handleOverlayClose={() => {
                            setShippingDetails('suggestions', []);
                            setShippingDetails('showSuggestions', false);
                          }}
                        />
                      )}
                  </Suspense>
                )}

                {showShippingMoqReviewWarning && (
                  <StoreCartPriceChangeWarning
                    variant="alert"
                    className={`${styleClass}__shipping-moq-review-warning`}
                  >
                    {STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_PREFIX}{' '}
                    <button
                      type="button"
                      className={`${styleClass}__shipping-moq-review-warning-action`}
                      onClick={handleShippingMoqReviewNowClick}
                    >
                      {STORE_SHIPPING_MOQ_PRICING_REVIEW_WARNING_ACTION_LABEL}
                    </button>
                  </StoreCartPriceChangeWarning>
                )}
              </div>
            </div>
          </FormProvider>
        </div>
      ),
      icon: tickIcon(),
      className: (() => {
        const shippingOption = shippingDetailsForm.watch('shippingOption');
        return shippingOption ? 'step-completed' : '';
      })(),
    },
  ];

  const paymentStep = [
    {
      title: (
        <FormProvider {...creditCardForm}>
          <Suspense>
            <SavedPaymentMethods
              methods={savedMethods}
              selectedToken={selectedSavedMethod?.token ?? null}
              isLoading={isSavedMethodsLoading}
              onSelect={handleSelectSavedPaymentMethod}
            />
            {/* Drop-in card fields are iframes; focus reaches the parent as a focus on the iframe. */}
            <div
              onPointerDownCapture={handleNewPaymentMethodInteraction}
              onFocusCapture={handleNewPaymentMethodInteraction}
            >
              <PayNow
                braintreeClientToken={checkoutBraintreeClientToken}
                isBraintreeEnabled={isBraintreeEnabled}
                isBraintreeUiLoading={isCheckoutBraintreeUiLoading}
                isBraintreeError={isAnonymousClientTokenError}
                total={total}
                onBackBtnClick={handleOrderInfoView}
                showVenmoMethod={isVenmoPaymentEnabled}
                consentGiven={consentGiven}
                showDropIn={!isSavedMethodsLoading}
                vaultOptInOnly
              />
            </div>

            {isBraintreeEnabled && !isUsingSavedPaymentMethod && !isSavedMethodsLoading ? (
              <SavePaymentMethodConsent
                id="store-checkout-save-payment-method"
                checked={Boolean(consentGiven)}
                onChange={handleConsentChange}
              />
            ) : null}
          </Suspense>
        </FormProvider>
      ),
      icon: tickIcon(),
    },
  ];

  if (showCollegiateInfoForm) {
    stepArray.splice(2, 0, {
      title: (
        <FormProvider {...universityApprovalForm}>
          <Suspense>
            <UniversityApproval />
          </Suspense>
        </FormProvider>
      ),
      icon: customIcon('/src/assets/svg/review.svg', 'icon--review'),
    });
  }

  /* Insert CustomizeOrderStep if there are any customized designs */
  if (customItem.length > 0) {
    const stepIndex = showCollegiateInfoForm ? 3 : 2;

    stepArray.splice(stepIndex, 0, {
      title: (
        <FormProvider {...csvFileForm}>
          <Suspense fallback={<div>Loading...</div>}>
            <CustomizeOrderStep items={customItem} />
          </Suspense>
        </FormProvider>
      ),
      icon: customIcon('/src/assets/svg/review.svg', 'icon--review'),
    });
  }

  /** Renders store type banner */
  const renderStoreBanner = () => {
    const selectedStoreType = storeTypeForm.watch('storeType');

    if (!selectedStoreType) {
      return null;
    }

    // Map store type to banner content
    const storeBannerConfig = {
      'group-order': {
        icon: '/src/assets/svg/group_order.svg',
        text: 'This store is a Group Order!',
      },
      fundraiser: {
        icon: '/src/assets/svg/fundraiser.svg',
        text: 'This store is a Fundraiser!',
      },
      'brand-drop': {
        icon: '/src/assets/svg/brand_drop.svg',
        text: 'This store is a Brand Drop!',
      },
      'permanent-store': {
        icon: '/src/assets/svg/permanent_store.svg',
        text: 'This store is a Permanent Store!',
      },
    };

    const currentBanner = storeBannerConfig[selectedStoreType as keyof typeof storeBannerConfig];

    if (!currentBanner) {
      return null;
    }

    return (
      <div className={`${styleClass}__store-banner`}>
        <div className={`${styleClass}__store-banner-content`}>
          <div className={`${styleClass}__store-banner-icon`}>
            <Icon source={currentBanner.icon} width="20px" height="20px" />
          </div>
          <span className={`${styleClass}__store-banner-text`}>{currentBanner.text}</span>
        </div>
      </div>
    );
  };

  /** Renders order summary component */
  const renderOrderSummary = () => (
    <FormProvider {...summaryForm}>
      <div
        ref={showMobileSummary ? null : orderSummaryRef}
        className={`${styleClass}__order-summary ${styleClass}__order-summary-flex`}
      >
        {/* Store Banner - Desktop only (mobile/tablet render it outside in overlay) */}
        {!showMobileSummary && renderStoreBanner()}
        <OrderSummary
          items={reviewItems}
          sizeFormValues={{ sizes: {} }}
          shippingFormValues={shippingDetailsFormValue as IRegularOrderShippingForm}
          showOrderSummary={isSizesValid || isSizesFormValid}
          showShippingDetails={isShippingDetailsValid}
          enablePlaceOrder={canPlaceOrder}
          priceZeroCartItem={priceZeroCartItem}
          onSubmit={createOrder}
          setSalesTaxCert={setSalesTaxCert}
          salesTaxCert={salesTaxCert}
          isLoading={isLoading}
          estimatedDeliveryDate={getNewTimelineShippingRanges(
            cartItemsInfo?.length > 0 ? cartItemsInfo[0]?.timelineData ?? [] : [],
            shipTypeChanged ?? 'standard',
            !getGuaranteedValue(),
            getLatestRestockDate(activeItemRestockDate ?? {}),
            false,
            false,
            !getGuaranteedValue(),
            timelineChangesEnabled
          )}
          promoFocus={false}
          promoCodeData={promoCodeData}
          promoApplied={promoApplied}
          onPromoApplyClick={onPromoApplyClick}
          onPromoRemoveClick={onPromoRemoveClick}
          promoCodeError={promoCodeError}
          setPromoCodeError={setPromoCodeError}
          showConfetti={showConfetti}
          showBraintreePaymentScreen={
            showBraintreePaymentScreen
          } /** This helps us to show the braintree Drop In UI in the last step. */
          continueToPay={continueToPay}
          isDropInInitialized={isUsingSavedPaymentMethod || isDropInInitialized}
          isBraintreeFormValid={isUsingSavedPaymentMethod || isFormValid}
          isPayLater={isPayLater}
          isBraintreeEnabled={isBraintreeEnabled}
          isStoreCheckout
          selectedPaymentOption={reviewOrderFormValue.paymentOption || ''}
          designGroups={designGroups}
          storeCloseDate={storeInfoFormValue.orderUntil || ''}
          storeLaunchTooltipTitle={storeLaunchTooltipTitle}
          storeEstimatedDeliveryRange={storeEstimatedDeliveryRange}
          selectedDeliveryShipType={shippingDetailsFormValue.shipTypeKey}
          onDeliveryShipTypeSelect={handleDeliveryShipTypeSelect}
          onPreviewStore={handlePreviewStore}
          canPreviewStore={canPreviewStore}
        />
      </div>
    </FormProvider>
  );

  return (
    <ErrorBoundary>
      <div
        className={`${styleClass} ${showBraintreePaymentScreen ? 'payment-screen-step' : ''} ${
          uiVariationCtx
            ? getStoreCheckoutUiVariationModifierClass(styleClass, uiVariationCtx.uiVariation)
            : ''
        }`}
        id="store-checkout-id"
      >
        {orderProcessing && (
          <Spinner
            tip={hasStoreIdInUrl ? STORE_CHECKOUT_LAUNCHING_TIP : STORE_CHECKOUT_PLACING_ORDER_TIP}
            size="default"
            className="store-checkout-loader"
          />
        )}

        {validatingAddress && (
          <Spinner
            tip="Validating the shipping address... Please wait."
            size="default"
            className="store-checkout-loader"
          />
        )}

        {!showBraintreePaymentScreen &&
          showMobileSummary &&
          !(isMobile && showMobileSummaryOverlay) && (
            <StoreCheckoutHeader {...storeCheckoutHeaderProps} />
          )}

        <div className={`${styleClass}__body`}>
          {/* Steps Section */}
          <div ref={stepsWrapperRef} className={`${styleClass}__steps-wrapper`}>
            {!showBraintreePaymentScreen && !showMobileSummary && (
              <StoreCheckoutHeader {...storeCheckoutHeaderProps} />
            )}
            {showBraintreePaymentScreen && (
              <div className={`${styleClass}__payment-flow braintree-payment-step`}>
                <StepProgress stepArray={paymentStep} currentStep={stepNumber} keepOriginalIcons />
              </div>
            )}
            {!showBraintreePaymentScreen && (
              <StepProgress
                stepArray={stepArray}
                currentStep={storeCheckoutProgressStep}
                keepOriginalIcons
              />
            )}
          </div>

          {/* Order Summary section (Desktop) */}
          {!showMobileSummary && renderOrderSummary()}
        </div>

        {/* Promo code applied overlay for mobile */}
        {mobPromoAppliedOverlay && (
          <PromoAppliedOverlay
            mobPromoAppliedOverlay={mobPromoAppliedOverlay}
            onClose={onMobPromoClose}
            promoCodeData={promoCodeData}
            showConfetti={showConfetti}
          />
        )}

        {/* Mobile bottom bar — matches /store-cart layout */}
        {showMobileSummary && isMobile && !showMobileSummaryOverlay && (
          <div className={`${styleClass}__bottom-fixed`}>
            <button
              type="button"
              className={`${styleClass}__bottom-fixed__summary-link`}
              onClick={() => setShowMobileSummaryOverlay(true)}
            >
              <Text as="span" size={14} color="#4698EA" weight="semibold">
                {STORE_CHECKOUT_FOOTER_SUMMARY_TEXT_MOBILE}
              </Text>
              <Icon
                source="/src/assets/svg/chev_down.svg"
                className={`${styleClass}__bottom-fixed__summary-link__icon`}
                height="7px"
                width="12px"
              />
            </button>
            <div className={`${styleClass}__bottom-bar`}>
              {showBraintreePaymentScreen && (
                <Button
                  className={`${styleClass}__bottom-bar__action-btn`}
                  iconClassName={`${styleClass}__bottom-bar__action-btn__icon`}
                  labelClassName={`${styleClass}__bottom-bar__action-btn__label`}
                  iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                  disabled={!canPlaceOrder || !isBraintreePaymentReady}
                  onClick={createOrder}
                >
                  {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT_MOBILE}
                </Button>
              )}

              {isBraintreeEnabled && !showBraintreePaymentScreen && !isPayLater && (
                <ToolTip
                  title={storeLaunchTooltipTitle}
                  overlayInnerStyle={STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE}
                >
                  <Button
                    className={`${styleClass}__bottom-bar__action-btn`}
                    iconClassName={`${styleClass}__bottom-bar__action-btn__icon`}
                    labelClassName={`${styleClass}__bottom-bar__action-btn__label`}
                    iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                    disabled={!canPlaceOrder}
                    onClick={continueToPay}
                  >
                    {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT_MOBILE}
                  </Button>
                </ToolTip>
              )}

              {(isPayLater || !isBraintreeEnabled) && (
                <ToolTip
                  title={storeLaunchTooltipTitle}
                  overlayInnerStyle={STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE}
                >
                  <Button
                    className={`${styleClass}__bottom-bar__action-btn`}
                    iconClassName={`${styleClass}__bottom-bar__action-btn__icon`}
                    labelClassName={`${styleClass}__bottom-bar__action-btn__label`}
                    iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                    disabled={!canPlaceOrder}
                    onClick={createOrder}
                  >
                    {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT_MOBILE}
                  </Button>
                </ToolTip>
              )}

              <div className={`${styleClass}__bottom-bar__description`}>
                <Text as="p" size={12} color="#8D90AA" weight="regular">
                  {STORE_CHECKOUT_MOBILE_FOOTER_DESCRIPTION}
                </Text>
              </div>
            </div>
          </div>
        )}

        {/* Tablet footer */}
        {showMobileSummary && !isMobile && (
          <div className={`${styleClass}__footer`}>
            <div className={`${styleClass}__footer-tablet`}>
              <div className={`${styleClass}__footer-tablet-col1`}>
                <div className={`${styleClass}__footer-meta`}>
                  <button
                    type="button"
                    className={`${styleClass}__order-summary-link`}
                    onClick={() => setShowMobileSummaryOverlay(!showMobileSummaryOverlay)}
                  >
                    {STORE_CHECKOUT_FOOTER_SUMMARY_TEXT}
                  </button>
                  <div className={`${styleClass}__footer-store-type`}>
                    {(() => {
                      const selectedStoreType = storeTypeForm.watch('storeType');
                      if (!selectedStoreType) {
                        return null;
                      }

                      const storeTypeConfig: {
                        [key: string]: { label: string; icon: string };
                      } = {
                        'group-order': {
                          label: 'Group Order',
                          icon: '/src/assets/svg/group_order.svg',
                        },
                        fundraiser: {
                          label: 'Fundraiser',
                          icon: '/src/assets/svg/fundraiser.svg',
                        },
                        'brand-drop': {
                          label: 'Brand Drop',
                          icon: '/src/assets/svg/brand_drop.svg',
                        },
                        'permanent-store': {
                          label: 'Permanent Store',
                          icon: '/src/assets/svg/permanent_store.svg',
                        },
                      };
                      const currentConfig = storeTypeConfig[selectedStoreType];
                      if (!currentConfig) {
                        return null;
                      }

                      return (
                        <div className={`${styleClass}__footer-store-type__content`}>
                          <Icon source={currentConfig.icon} width="20px" height="20px" />
                          <Text as="span" className={`${styleClass}__footer-store-type__text`}>
                            {currentConfig.label}
                          </Text>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Confirm Button */}
              <div className={`${styleClass}__footer-tablet-col3`}>
                {showBraintreePaymentScreen && (
                  <Button
                    className={`${styleClass}__footer-tablet-confirm-btn`}
                    iconClassName="order-summary__footer__icon"
                    labelClassName="order-summary__footer__label"
                    iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                    disabled={!canPlaceOrder || !isBraintreePaymentReady}
                    onClick={createOrder}
                  >
                    {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT}
                  </Button>
                )}
                {isBraintreeEnabled && !showBraintreePaymentScreen && !isPayLater && (
                  <ToolTip
                    title={storeLaunchTooltipTitle}
                    overlayInnerStyle={STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE}
                  >
                    <Button
                      className={`${styleClass}__footer-tablet-confirm-btn`}
                      iconClassName="order-summary__footer__icon"
                      labelClassName="order-summary__footer__label"
                      iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                      disabled={!canPlaceOrder}
                      onClick={continueToPay}
                    >
                      {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT}
                    </Button>
                  </ToolTip>
                )}
                {(isPayLater || !isBraintreeEnabled) && (
                  <ToolTip
                    title={storeLaunchTooltipTitle}
                    overlayInnerStyle={STORE_LAUNCH_DISABLED_TOOLTIP_INNER_STYLE}
                  >
                    <Button
                      className={`${styleClass}__footer-tablet-confirm-btn`}
                      iconClassName="order-summary__footer__icon"
                      labelClassName="order-summary__footer__label"
                      iconOnRightSrc="/src/assets/svg/double_arrow.svg"
                      disabled={!canPlaceOrder}
                      onClick={createOrder}
                    >
                      {isEditPage ? 'Save Changes' : STORE_ORDER_SUMMARY_TEXT}
                    </Button>
                  </ToolTip>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order Summary Overlay (Mobile & Tablet) */}
        {showMobileSummaryOverlay && !mobPromoAppliedOverlay && (
          <Overlay
            isOpen={showMobileSummaryOverlay}
            modalStyles={
              isMobile ? mobileSummaryOverlayProps.modalStyles : TABLET_SUMMARY_OVERLAY_STYLE
            }
            overlayStyles={isMobile ? mobileSummaryOverlayProps.overlayStyles : undefined}
            closeIconWrapperClass="summary-close-icon"
            closeIconPath="/src/assets/svg/close.svg"
            closeIconWrapperStyles={MOBILE_SUMMARY_CLOSE_ICON_STYLE}
            onCloseClick={() => setShowMobileSummaryOverlay(false)}
            onBackdropClick={() => setShowMobileSummaryOverlay(false)}
            modalClassName={`${styleClass}__order-summary-modal`}
          >
            {renderStoreBanner()}
            {renderOrderSummary()}
          </Overlay>
        )}

        {/* Floating Preview Store Button (Mobile & 992px-1244px range) */}
        {(showMobileSummary || (viewportWidth >= 992 && viewportWidth <= 1244)) &&
          !showMobileSummaryOverlay && (
            <button
              type="button"
              className={`${styleClass}__preview-store-button`}
              onClick={handlePreviewStore}
              disabled={!canPreviewStore}
            >
              Preview Store
              <span>👀</span>
            </button>
          )}

        {!isEditPage && (
          <Overlay
            isOpen={isNewStoreLeaveModalOpen}
            closeIconWrapperStyles={{
              position: 'absolute',
              top: 16,
              right: 16,
              padding: 0,
              cursor: 'pointer',
              zIndex: 2,
            }}
            closeIconPath="/src/assets/svg/close.svg"
            closeIconWrapperClass="store-edit-unsaved-changes-modal-close"
            modalClassName="store-edit-unsaved-changes-modal"
            overlayClassName="store-edit-unsaved-changes-modal-overlay"
            modalStyles={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              padding: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              zIndex: STORE_EDIT_LEAVE_MODAL_Z_INDEX + 1,
            }}
            overlayStyles={{
              ...defaultOverlayStyles,
              zIndex: STORE_EDIT_LEAVE_MODAL_Z_INDEX,
            }}
            onCloseClick={handleNewStoreLeaveModalClose}
            onBackdropClick={handleNewStoreLeaveModalClose}
          >
            <Suspense fallback={null}>
              <ConfirmationModal
                primaryText={STORE_NEW_SETUP_LEAVE_MODAL_PRIMARY_TEXT}
                primaryTextSize={20}
                primaryTextWeight="heavy"
                cancelLabel={STORE_NEW_SETUP_LEAVE_MODAL_DISCARD_LABEL}
                confirmLabel={STORE_NEW_SETUP_LEAVE_MODAL_CONTINUE_LABEL}
                confirmVariant="primary"
                onCancel={handleNewStoreLeaveModalDiscard}
                onProcess={handleNewStoreLeaveModalClose}
              />
            </Suspense>
          </Overlay>
        )}
      </div>
    </ErrorBoundary>
  );
};

StoreCheckout.displayName = 'StoreCheckout';

export default StoreCheckout;
